<script setup lang="ts">
import { ref, onBeforeMount, inject, Ref, watch, reactive } from 'vue';
import { storeToRefs } from 'pinia';
import { getCurrentLocale } from '@/i18n';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import { UNASSIGNED, BUSINESS, OPENJAW, ONEWAY } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useComponentComposable } from '@/components/useComponentComposable';
import { useWorksheetComposable } from './useWorksheetComposable';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { useAirManagementStore } from '@/store/AirManagementStore';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import cloneDeep from 'lodash/cloneDeep';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import DataTable from 'primevue/datatable';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import InputTextAera from 'primevue/textarea';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import HotelInform from '@/components/modal/worksheet/HotelInform.vue';
import HTMLEditor from '@/components/common/HTMLEditor.vue';
import LinkedQuotesEditor from '@/components/modal/worksheet/LinkedQuotesEditor.vue';
import ListFlightModal from '../ListFlightModal.vue';
import PriceBreakdownModal from '../PriceBreakdownModal.vue';
import ViewFlightSeat from './ViewFlightSeat.vue';
import ViewPDFFile from '@/components/modal/utils/ViewPDFFile.vue';

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const dialogRef = inject('dialogRef') as Ref;
const reloadList = dialogRef.value.options.emits.reloadList;
const dialog = useDialog();
const airManagementStore = useAirManagementStore();

const toast = useToast();

const {
  authorizations,
  canViewPromotion,
  canEditPromotion,
  canViewAgencyComment,
  canEditAgencyComment,
  canViewTermConditions,
  canEditTermConditions,
  hasUserPermission,
  validateFlightNumber,
  airportList,
  initFlightData,
  formatFlightData,
  onFilterAirport,
  LIST_CABIN,
  getCabinName,
  semistarImage,
  handleRedirectLink,
  isCheckValidateDate,
  handleConvertBrTag,
  handleParseFloat,
  breakdownPrice,
  formatPriceRow,
  changePricingFlight,
  handleChildInput,
  checkEditChildInput
} = useWorksheetComposable();

const { fallBackImage } = useComponentComposable();
const adminQuoteStore = useAdminQuoteStore();
const editingRows = ref([]);
const editingRowsHotelPre: any = ref({});
const editingRowsHotelPost: any = ref({});
const cabinEditingRows = ref([]);
const listAirline = ref([]);
const filterCruiseGateway = ref([]);
const filterCruiseDestination = ref([]);
const listOutbound: any = ref([]);
const listInbound: any = ref([]);
const disablePreviewPDF = ref(true);
const isLoadingButton = ref(false);
const isShowError = ref(false);
let detailClasses: any[] = [];
const expandedDetail = ref<any>([[]]);
const validationInputs = reactive<Record<string, boolean>>({});
let typeProcess = [['Economy']];
const { currentQuote: quote } = storeToRefs(adminQuoteStore);

const promoEditedData = ref({
  index: null,
  data: ''
});

const editTransfer = ref({
  bool: false,
  data: ''
});
const editComment = ref({
  index: null,
  data: ''
});

const editIntro = ref({
  isEdit: false,
  data: ''
});

const editTermConditions = ref({
  isEdit: false,
  data: ''
});

const editAdult = ref({
  isEdit: false,
  data: ''
});

const editChild = ref({
  isEdit: false,
  data: ''
});

const editAdultFlight = ref({
  isEdit: false,
  data: ''
});

const editChildFlight = ref({
  isEdit: false,
  data: ''
});

const editAdultBusinessFlight = ref({
  isEdit: false,
  data: ''
});

const editChildBusinessFlight = ref({
  isEdit: false,
  data: ''
});

const editAdultPreHotel = ref({
  isEdit: false,
  data: ''
});

const editChildPreHotel = ref({
  isEdit: false,
  data: ''
});

const editAdultPostHotel = ref({
  isEdit: false,
  data: ''
});

const editChildPostHotel = ref({
  isEdit: false,
  data: ''
});

const editNumberOfRoomPreHotel = ref({
  isEdit: false,
  data: ''
});

const editNumberOfRoomPostHotel = ref({
  isEdit: false,
  data: ''
});

const canViewFlight = authorizations?.worksheet_cruise?.flight_view;
const canEditFlight = authorizations?.worksheet_cruise?.flight_edit;
const canEditHeader = authorizations?.worksheet_cruise?.quote_details_header_edit;

const expandCruiseFlightPricing = (type: string, cruiseIndex: number, index: number) => {
  expandedDetail.value[cruiseIndex][index][type] = !expandedDetail.value[cruiseIndex][index][type];
  typeProcess[cruiseIndex][index] = type;
};

const handleSaveAdult = (adultNo: any, data: any, key: string, initData: any, flights?: any) => {
  data[key] = adultNo;
  initData.isEdit = false;
  if (flights) {
    formatFlights(flights, key, adultNo);
  }
};

const handleSaveChild = (childNo: any, data: any, key: string, initData: any, flights?: any) => {
  data[key] = childNo;
  initData.isEdit = false;
  if (flights) {
    formatFlights(flights, key, childNo);
  }
};

const handleSaveNumberOfRoom = (numOfRooms: any, data: any, key: string, initData: any) => {
  data[key] = numOfRooms;
  initData.isEdit = false;
};

const getTime = (date: any) => {
  return date ? DateTime.fromJSDate(new Date(date)).toFormat('T') : '';
};

const saveTransfer = (cruise: any) => {
  cruise.transfers = editTransfer.value.data;
  editTransfer.value.bool = false;
};

const saveComment = (value: any, key: string) => {
  value[key] = editComment.value.data;
  editComment.value = { index: null, data: '' };
};

const handleSavePromo = (value: any, key: string) => {
  value[key] = promoEditedData.value.data;
  promoEditedData.value = { index: null, data: '' };
};

const handleSaveIntro = (cruiseData: any) => {
  cruiseData.intro = editIntro.value.data;
  editIntro.value = {
    isEdit: false,
    data: ''
  };
};

const handleSaveTermCondition = (cruiseData: any) => {
  cruiseData.termConditions = editTermConditions.value.data;
  editTermConditions.value = {
    isEdit: false,
    data: ''
  };
};

const isProcess = (cruise: any, type: string, index: string) => {
  const cruiseType = cruise?.flightPrices.map((flPrice: any) => flPrice.type);
  const indexOf = cruiseType.indexOf(type);
  return index === indexOf;
};

const formatHeaderPricing = (cruise: any, pricing: any, index: number) => {
  let adtTemp = cruise.flights[0].adt ?? 0;
  let chdTemp = cruise.flights[0].chd ?? 0;
  if (pricing.type.toUpperCase() === BUSINESS) {
    adtTemp = cruise.flights[0].businessAdt ?? 0;
    chdTemp = cruise.flights[0].businessChd ?? 0;
  }
  const typeKey = `worksheet.${pricing.type.toLowerCase()}`;
  const type = `${t('common.pricing')} (${t(typeKey)})`;
  const adt = `${adtTemp} ${t('common.adults')}`;
  const children = `${chdTemp} ${t('common.children')}`;
  let result = `${type} (${adt}, ${children})`;
  if (cruise.flightPrices[index].suffixKey) {
    result = `${type} ${cruise?.flightPrices?.[index].numberOfPassengers ? formatFareBasic(cruise.flightPrices[index]) : ''}`;
  }
  return result;
};

const formatFareBasic = (flPrice: any) => {
  const fares = flPrice.fareBasis.split(' / ');
  return `-${fares.map((fare: any) => fare.charAt(0)).join('/')} - ${flPrice.numberOfPassengers} ${t('client_copy.number_of_passengers').toLowerCase()}`;
};

const formatFlightPrice = (priceList: any, permission: any) => {
  const listPricing: any = [];
  priceList.forEach((item, pIndex) => {
    listPricing.push({
      type: item.type || item.cabin,
      comment: item.comment,
      promotions: item.promotions,
      detail: item.detail,
      pIndex,
      rows: formatPriceRow(item, permission)
    });
  });
  return listPricing;
};

const cabinProperty = ['singlePrice', 'doublePrice', 'triplePrice', 'quadPrice', 'fiveInRoomPrice', 'sixInRoomPrice', 'childOnePrice', 'childTwoPrice', 'childThreePrice', 'infantPrice'];
const formatCruiseData = (cabins, permission) => {
  return cabins.map((cabin) => {
    const cabinData = { ...cabin, cabinRows: [] };
    if (permission.agency_selling_price_view) {
      cabinData.cabinRows.push({ label: 'worksheet.agency_selling_price', key: 'sellingPrice', ...cabinData.sellingPrice, canEdit: permission.agency_selling_price_edit });
    }
    if (permission.agency_tax_view) {
      cabinData.cabinRows.push({ label: 'worksheet.agency_taxes', key: 'taxes', ...cabinData.taxes, canEdit: permission.agency_tax_edit });
    }
    if (permission.nccf_view) {
      cabinData.cabinRows.push({ label: 'worksheet.nccf', key: 'fare', ...cabinData.fare, canEdit: permission.nccf_edit });
    }

    const totalRow = {};
    cabinProperty.forEach((property) => {
      totalRow[property] = cabinData.cabinRows.reduce((total, current) => Number(current[property]) + total, 0);
    });
    cabinData.cabinRows.push({ label: 'worksheet.total_selling', key: 'total', ...totalRow });
    cabinData.cabinRows.unshift({ label: '', key: 'occupancies', ...cabinData.occupancies, canEdit: authorizations?.quote_management?.edit_age_ranges });
    return cabinData;
  });
};

const getHotelPrice = (price) => {
  return [
    { name: '', key: 'occupanciesEdit' },
    { name: 'worksheet.agency_selling_price', key: 'sellingPrice' },
    { name: 'worksheet.agency_taxes', key: 'taxes' }
  ].map((item) => ({
    key: item.key,
    name: item.name,
    ...price[item.key]
  }));
};

const onRowEditSave = ({ newData, index }, value) => {
  let depTime = newData?.depTime?.toString();
  if (depTime?.length > 5) {
    depTime = getTime(depTime);
  }

  let arrTime = newData?.arrTime?.toString();
  if (arrTime?.length > 5) {
    arrTime = getTime(arrTime);
  }

  if (typeof newData.dest === 'object') {
    newData.destCode = newData.dest.airportCode;
    newData.dest = `${newData.dest.airportName} (${newData.dest.airportCode})`;
  }

  if (typeof newData.gate === 'object') {
    newData.gateCode = newData.gate.airportCode;
    newData.gate = `${newData.gate.airportName} (${newData.gate.airportCode})`;
  }
  if (isCheckValidateDate(newData, value)) {
    newData.isNew = false;

    value[index] = { ...newData, depTime, arrTime };
    value[index].airline = listAirline.value.find((air) => air.code === value[index].operated)?.name;
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('worksheet.validate_date') });
  }
};

const changePricingCabinCruise = ({ newData }, price) => {
  cabinProperty.forEach((property) => {
    price[newData.key][property] = newData[property];
  });
};

const formatFlights = (flights: any, key: string, data: number) => {
  return flights.map((flight: any) => {
    flight[key] = data;
    return flight;
  });
};

const updatePriceObject = (priceObject: any) => {
  Object.keys(priceObject).forEach((key: any) => {
    const priceEntry = priceObject[key];
    if (!priceEntry.childPrices || !Array.isArray(priceEntry.childPrices)) {
      return;
    }

    if (priceEntry.childPrices.length === 0) {
      priceEntry.childPrices = ['0.00', '0.00', '0.00'];
    } else {
      priceEntry.childPrices = [priceEntry.childOnePrice, priceEntry.childTwoPrice, priceEntry.childThreePrice];
    }
  });
};

const updateRooms = (rooms: any) => {
  rooms.forEach((room: any) => {
    if (room.price) {
      updatePriceObject(room.price);
    }
  });
};

const updateHotels = (hotels: any) => {
  hotels.forEach((hotel: any) => {
    hotel.hotels.forEach((hotelItem: any) => {
      if (hotelItem.rooms) {
        updateRooms(hotelItem.rooms);
      }
    });
  });
};

const updateChildPrices = (data: any) => {
  data.forEach((item: any) => {
    if (item.preHotel) {
      updateHotels(item.preHotel);
    }
    if (item.postHotel) {
      updateHotels(item.postHotel);
    }
  });
};

const convertPayloadSubmit = (quoteData: any) => {
  const data = cloneDeep(quoteData);
  data.cruises.forEach((cruise: any) => {
    cruise.flightPrices.forEach((item: any) => {
      ['basePrice', 'paxFareSumAdult', 'paxFareSumChild'].forEach((key) => delete item[key]);
    });
    cruise.flights.forEach((flight: any) => {
      if (flight.cabin && flight.cabin.length && typeof flight.cabin !== 'string') {
        flight.cabin = flight.cabin.join(', ');
      }
    });
  });
  return data;
};

const handleUpdateQuote = (quoteData: any) => {
  const { qType, cruises, sk, expires, bookedDate, intro, termConditions } = convertPayloadSubmit(quoteData);
  updateChildPrices(cruises);
  const payload = {
    qType,
    cruises,
    intro: handleConvertBrTag(intro),
    termConditions: handleConvertBrTag(termConditions),
    expires: new Date(formatDate(expires, undefined, false)),
    bookedDate: new Date(formatDate(bookedDate, undefined, false)),
    linkedQuotes: quote.value?.linkedQuotes
  };
  adminQuoteStore
    .updateQuote(payload, sk)
    .then(() => {
      disablePreviewPDF.value = false;
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      reloadList();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: 'Error Message', detail: t('notification.save_error'), life: 5000 });
    });
};

const changePriceHotel = ({ newData }, price) => {
  const { key, name, ...rest } = newData;
  newData.name = name;
  price[key] = rest;
  handleFormatDataHotel();
};

const editExpiryDate = ref({
  isEdit: false,
  data: null
});

const editDateBooked = ref({
  isEdit: false,
  data: null
});

const getCabinCode = (cruiseCode: string) => {
  let cabinString = '';
  switch (cruiseCode) {
    case 'OS':
      cabinString = t('worksheet.outside');
      break;
    case 'BA':
      cabinString = t('worksheet.balcony');
      break;
    case 'SU':
      cabinString = t('worksheet.outside');
      break;
    default:
      cabinString = t('worksheet.inside');
      break;
  }
  return cabinString;
};

const formatPricingHead = (cabinCategory: any, cabinCode: string) => {
  const foundCabin = cabinCategory.filter((cabin: any) => cabin.code === cabinCode);
  return foundCabin.length && (foundCabin[0].nameEn || foundCabin[0].nameFr) ? `${getTextVariant(currentLanguage, foundCabin[0].nameEn, foundCabin[0].nameFr)} - ${foundCabin[0].code}` : getCabinCode(cabinCode);
};

const checkOldArray = (cruiseData: any) => {
  return Array.isArray(cruiseData) && cruiseData.every((item: any) => item.categoryId === null || item.nameEnd === null || item.nameFr === null);
};

const formatCruiseCategory = (cruiseData: any) => {
  const listCategory: any = [];
  if (checkOldArray(cruiseData)) {
    cruiseData.forEach((cruise: any) => {
      switch (cruise.code) {
        case 'IN':
          listCategory.push(t('worksheet.inside'));
          break;
        case 'OS':
          listCategory.push(t('worksheet.outside'));
          break;
        case 'BA':
          listCategory.push(t('worksheet.balcony'));
          break;
        case 'SU':
          listCategory.push(t('worksheet.suite'));
          break;
        default:
          break;
      }
    });
    return listCategory.toString();
  } else {
    return cruiseData.length ? cruiseData.map((cabin: any) => `${getTextVariant(currentLanguage, cabin.nameEn, cabin.nameFr)} - ${cabin.code}`).join(', ') : '';
  }
};

const addNewFlight = (cruise, type) => {
  const groupFlights = cruise.flights.filter((flight) => flight.type === type);
  const numberOfFlight = groupFlights?.length;
  if (numberOfFlight < 4) {
    let lastIndex = -1;
    cruise.flights?.forEach((flight, index) => {
      if (flight.type === type) {
        lastIndex = index;
      }
    });
    const initFlightKey = `flight${Date.now()}${cruise.flights.length}`;
    cruise.flights.splice(lastIndex + 1, 0, initFlightData(type, initFlightKey));
    editingRows.value = cruise.flights.filter((flight) => editingRows.value.find((row) => row.key === flight.key) || flight.key === initFlightKey);
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('worksheet.maximum_flight_notification') });
  }
};

const deleteFlight = (cruise, flight) => {
  cruise.flights = cruise.flights.filter((f) => f.key !== flight.key);
};

const openPriceBreakModal = (price: any) => {
  const dialogRef = dialog.open(PriceBreakdownModal, {
    props: {
      header: t('worksheet.price_breakdown'),
      modal: true
    },
    data: {
      getPostSearch: price.breakdownPrice,
      isHiddenChild: true,
      isHiddenInfant: true
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const formatPrice = (price: number) => {
  return new Intl.NumberFormat(currentLanguage, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price);
};

const searchCruiseGateway = (event) => {
  if (!event.query.trim().length || event.originalEvent.type !== 'input') {
    filterCruiseGateway.value = [...airportList.value.slice(0, 100)];
  } else {
    filterCruiseGateway.value = airportList.value.filter((air) => {
      return onFilterAirport(air, event);
    });
  }
};

const searchCruiseDestination = (event) => {
  if (!event.query.trim().length || event.originalEvent.type !== 'input') {
    filterCruiseDestination.value = [...airportList.value.slice(0, 100)];
  } else {
    filterCruiseDestination.value = airportList.value.filter((air) => {
      return onFilterAirport(air, event);
    });
  }
};

const handleSaveExpiry = (date) => {
  quote.value.expires = date;
  editExpiryDate.value.isEdit = false;
};

const handleSaveDateBooked = (date) => {
  quote.value.bookedDate = date;
  editDateBooked.value.isEdit = false;
};

const changeLinkedQuoteCruise = (event: any) => {
  quote.value.linkedQuotes = event;
};

const getFlighstClass = (flights: any, cruiseIndex: any) => {
  flights?.forEach((flight: any) => {
    if (flight.type === 'Outbound') {
      listOutbound.value[cruiseIndex].push(flight);
    } else if (flight.type === 'Inbound') {
      listInbound.value[cruiseIndex].push(flight);
    }
  });
};

const validateFlightData = (flightsData) => {
  return flightsData.every((flight) => Object.values(flight).every((flightKey) => flightKey != null && flightKey !== ''));
};

const handleOpenViewFlightSeat = (cruise: any, tabIndex: number) => {
  const flightTemp = [];
  cruise.flights?.forEach((flight) => {
    const flightOption = {
      type: flight.type,
      gateCode: listOutbound.value[tabIndex]?.[0]?.gateCode,
      destCode: listOutbound.value[tabIndex]?.[listOutbound.value[tabIndex]?.length - 1]?.destCode,
      depDate: formatDate(listOutbound.value[tabIndex]?.[0]?.depDate, undefined, false),
      retDate: formatDate(listInbound.value[tabIndex]?.[0]?.depDate, undefined, false),
      direct: !!flight.direct,
      airlineCode: flight.airlineCode,
      number: flight.flightNumber
    };
    flightTemp.push(flightOption);
  });
  const payload = [
    {
      gateCode: listOutbound.value[tabIndex]?.[0]?.gateCode,
      destCode: listOutbound.value[tabIndex]?.[listOutbound.value[tabIndex]?.length - 1]?.destCode,
      depDate: formatDate(listOutbound.value[tabIndex]?.[0]?.depDate, undefined, false),
      retDate: formatDate(listInbound.value[tabIndex]?.[0]?.depDate, undefined, false),
      direct: listOutbound.value[tabIndex][0]?.direct,
      flightRequest: flightTemp,
      option: `${+tabIndex + 1}`
    }
  ];
  isLoadingButton.value = true;
  const validateFlightPayload = validateFlightData(flightTemp);
  if (validateFlightPayload) {
    airManagementStore
      .getFlightSeat(payload)
      .then((res) => {
        const dialogRef = dialog.open(ViewFlightSeat, {
          props: {
            modal: true,
            header: t('worksheet.flight_details'),
            style: {
              width: '95vw'
            }
          },
          data: {
            flightInfo: cruise.flights,
            flightSeatInfo: res,
            isCruise: true
          },
          emits: {
            onClose: () => {
              dialogRef.close();
            }
          }
        });
      })
      .catch(() => {
        toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
      })
      .finally(() => {
        isLoadingButton.value = false;
      });
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('notification.please_fill_field') });
    isLoadingButton.value = false;
  }
};

const filterFlightFollowType = (flights: any, itinerary: string) => {
  return itinerary === ONEWAY ? flights.filter((flight: any) => flight.type === 'Outbound') : flights;
};

const calculateTotalPassenger = (cruise: any, type: string) => {
  return type == 'Economy' ? cruise.flights[0].adt + cruise.flights[0].chd : cruise.flights[0].businessAdt + cruise.flights[0].businessChd;
};

const simplifyMatchedClasses = (classes: string[]) => {
  const seen: string[] = [];
  const result: string[] = [];

  classes.forEach((code: string) => {
    const firstChar = code.charAt(0);
    if (!seen.includes(firstChar)) {
      seen.push(firstChar);
      result.push(firstChar);
    }
  });
  return result;
};

const openFlightModal = (cruise: any, cruiseIndex: number, pricing: any, pricingIndex: number, quote: any) => {
  for (let i = 0; i < cruise.flightPrices.length; i++) {
    if (!expandedDetail.value[cruiseIndex][i][typeProcess[cruiseIndex][i]]) {
      typeProcess[cruiseIndex][i] = '';
    }
  }
  const dialogRef = dialog.open(ListFlightModal, {
    props: {
      header: t('worksheet.flight_details'),
      style: { width: '90vw' },
      modal: true,
      maximizable: true
    },
    data: {
      air: cruise,
      airIndex: cruiseIndex,
      passenger: calculateTotalPassenger(cruise, pricing.type),
      pricing,
      pricingIndex,
      quote,
      role: authorizations?.worksheet_cruise
    },
    emits: {
      detailClassesEmit: (detail: any) => {
        updateDetailClasses(detail);
      },
      populateWorksheet: (data: any) => {
        updateData(data, cruiseIndex);
        dialogRef.close();
      }
    }
  });
};

const getRoomsName = (cruiseHotel: any) => {
  let result = '';
  cruiseHotel.rooms.forEach((room: any, indexRoom: number) => {
    if (indexRoom) {
      result += ',\n';
    }
    result += `${getTextVariant(currentLanguage, room.room, room.roomFr)} (${room.code})`;
  });
  return result;
};

const updateCruiseFlightsDetails = (airIndex: number, detailClasses: any, fareBasisCodes: string[], cruiseIndex: number) => {
  let cruiseDetails: any = {};
  if (quote.value.cruises[cruiseIndex]?.details) {
    cruiseDetails = JSON.parse(quote.value.cruises[cruiseIndex].details);
    for (const key in detailClasses[airIndex]) {
      if (Object.prototype.hasOwnProperty.call(detailClasses[airIndex], key)) {
        if (Object.prototype.hasOwnProperty.call(cruiseDetails, key)) {
          const detailTemp = [...cruiseDetails[key], ...detailClasses[airIndex][key]];
          cruiseDetails[key] = [...new Set(detailTemp)];
        } else {
          cruiseDetails[key] = detailClasses[airIndex][key];
        }
      }
    }
  } else {
    cruiseDetails = detailClasses[airIndex];
  }
  const result: any = {};
  for (const key in cruiseDetails) {
    if (Object.prototype.hasOwnProperty.call(cruiseDetails, key)) {
      result[key] = cruiseDetails[key].filter((item: string) => fareBasisCodes.includes(item));
    }
  }
  return result;
};

const updateData = async (data: any, cruiseIndex: any) => {
  const prices = data.classSelected.map((classSelect: any) => {
    const cruiseFlightPrice = cloneDeep(quote.value.cruises[cruiseIndex]?.flightPrices[data.pricingIndex]);
    cruiseFlightPrice.fareCharter = classSelect.data.charter;
    cruiseFlightPrice.fareVACV = classSelect.data.vac;
    cruiseFlightPrice.fareGroupsB = classSelect.data.groupsB;
    cruiseFlightPrice.farePub = classSelect.data.published;
    cruiseFlightPrice.numberOfPassengers = classSelect.data.numberOfPassengersFares;
    cruiseFlightPrice.fareTax = handleParseFloat(classSelect.data.taxes);
    cruiseFlightPrice.fare = handleParseFloat(classSelect.data.basePrice);
    cruiseFlightPrice.fareBasis = classSelect.data.fareBasis;
    cruiseFlightPrice.basePrice = classSelect.data.basePrice;
    cruiseFlightPrice.paxFareSumAdult = classSelect.data.paxFareSumAdult;
    cruiseFlightPrice.paxFareSumChild = classSelect.data.paxFareSumChild;
    cruiseFlightPrice.adt = handleParseFloat(classSelect.data.marginFare);
    cruiseFlightPrice.chd = handleParseFloat(classSelect.data.marginFare);
    cruiseFlightPrice.adtTax = handleParseFloat(classSelect.data.taxes);
    cruiseFlightPrice.chdTax = handleParseFloat(classSelect.data.taxes);
    cruiseFlightPrice.suffixKey = classSelect.data.suffixKey;
    cruiseFlightPrice.fareTypeSelected = classSelect.data.fareTypeSelected;
    cruiseFlightPrice.breakdownPrice = breakdownPrice(classSelect);
    cruiseFlightPrice.adtNo = 0;
    cruiseFlightPrice.chdNo = 0;
    cruiseFlightPrice.parties = [];
    return cruiseFlightPrice;
  });
  const typeFilter = quote.value.cruises[cruiseIndex].flightPrices.filter((price: any) => price.type === quote.value.cruises[cruiseIndex].flightPrices[data.pricingIndex].type);
  quote.value.cruises[cruiseIndex].flightPrices.splice(data.pricingIndex, typeFilter.length, ...prices);
  const fareBasisCodes = quote.value.cruises[cruiseIndex].flightPrices.flatMap((price: any) => price.fareBasis?.split(' / '));
  quote.value.cruises[cruiseIndex].details = updateCruiseFlightsDetails(data.airIndex, detailClasses, fareBasisCodes, cruiseIndex);
  quote.value.cruises[cruiseIndex].flights.forEach((flight: any) => {
    const { flightNo } = flight;
    if (typeof quote.value.cruises[cruiseIndex].details === 'object' && Object.prototype.hasOwnProperty.call(quote.value.cruises[cruiseIndex].details, flightNo)) {
      const classes = quote.value.cruises[cruiseIndex].details[flightNo];
      let fareClasses: any = [];
      fareBasisCodes.forEach((code: string) => {
        if (classes.includes(code)) {
          fareClasses.push(code);
        }
      });
      const cabins: any[] = [];
      quote.value.cruises[cruiseIndex].flightPrices.forEach((price: any) => {
        if (price.fareBasis) {
          const fareBasis = price.fareBasis.split(' / ');
          if (fareClasses.some((classes: string) => fareBasis.includes(classes)) && !cabins.includes(price.type)) {
            cabins.push(price.type);
          }
        }
      });
      flight.cabin = cabins;
      fareClasses = simplifyMatchedClasses(fareClasses);

      if (fareClasses.length) {
        flight.fClass = fareClasses.join(', ');
      }
    }
  });
  quote.value?.cruises.forEach((cruise: any) => {
    cruise.flights = formatFlightData(cruise.flights, true);
  });
  quote.value.cruises[cruiseIndex].details = JSON.stringify(quote.value.cruises[cruiseIndex].details);
};

const updateDetailClasses = (dataDetail: any) => {
  if (detailClasses.length && detailClasses[dataDetail.airIndex] && detailClasses[dataDetail.airIndex].length) {
    for (let i = 0; i < dataDetail.detailClasses.length; i++) {
      for (const key in dataDetail.detailClasses[i]) {
        detailClasses[i][key] = [...detailClasses[i][key], ...dataDetail.detailClasses[i][key]];
      }
    }
  } else {
    detailClasses = dataDetail.detailClasses;
  }
};

const setRoomDataPre = (hotelData: any, indexOption: number) => {
  hotelData?.preHotel.forEach((preHotel: any, indexPreHotel: number) => {
    editingRowsHotelPre.value[indexOption][indexPreHotel] = [];
    preHotel?.hotels.forEach((hotel: any, indexHotel: number) => {
      editingRowsHotelPre.value[indexOption][indexPreHotel][indexHotel] = [];
      hotel?.rooms.forEach((room: any, indexRoom: number) => {
        editingRowsHotelPre.value[indexOption][indexPreHotel][indexHotel][indexRoom] = [];
      });
    });
  });
};

const setRoomDataPost = (hotelData: any, indexOption: number) => {
  hotelData?.postHotel.forEach((postHotel: any, indexPostHotel: number) => {
    editingRowsHotelPost.value[indexOption][indexPostHotel] = [];
    postHotel?.hotels.forEach((hotel: any, indexHotel: number) => {
      editingRowsHotelPost.value[indexOption][indexPostHotel][indexHotel] = [];
      hotel?.rooms.forEach((room: any, indexRoom: number) => {
        editingRowsHotelPost.value[indexOption][indexPostHotel][indexHotel][indexRoom] = [];
      });
    });
  });
};

const handleFormatDataHotel = () => {
  quote.value?.cruises.forEach((option: any, indexOption: number) => {
    editingRowsHotelPost.value[indexOption] = {};
    editingRowsHotelPre.value[indexOption] = {};
    setRoomDataPre(option, indexOption);
    setRoomDataPost(option, indexOption);
  });
};

const handleChildCruiseInput = (value: any, field: string) => {
  isShowError.value = !handleChildInput(value, field, null, validationInputs);
};

const handleCancleChildInput = (editorCancelCallback: any) => {
  editorCancelCallback();
  isShowError.value = false;
};

const handleSaveChildInput = (editorSaveCallback: any, data: any) => {
  if (data.key !== 'occupancies' && data.key !== 'occupanciesEdit') {
    isShowError.value = false;
    editorSaveCallback();
  } else {
    checkEditChildInput(editorSaveCallback, isShowError.value);
  }
};

const handleSaveChildCruiseInput = (editorSaveCallback: any, data: any) => {
  if (data.key !== 'occupancies' && data.key !== 'occupanciesEdit') {
    editorSaveCallback();
  } else {
    checkEditChildInput(editorSaveCallback, isShowError.value);
  }
};

onBeforeMount(async () => {
  handleFormatDataHotel();
  listAirline.value = await adminQuoteStore?.getAirlines();
  quote.value?.cruises?.forEach(async (cruise: any, cruiseIndex: number) => {
    listOutbound.value[cruiseIndex] = [];
    listInbound.value[cruiseIndex] = [];
    cruise.flights = await formatFlightData(cruise.flights);
    getFlighstClass(cruise.flights, cruiseIndex);
  });
  editExpiryDate.value.data = quote.value?.expires && new Date(quote.value?.expires);
  editDateBooked.value.data = quote.value?.bookedDate && new Date(quote.value?.bookedDate);
});

watch(
  quote,
  (newVal) => {
    if (newVal.cruises) {
      typeProcess = Array(newVal.cruises.length).fill(['Economy']);
    }
    newVal.cruises?.forEach((cruise: any, indexCruise: number) => {
      expandedDetail.value[indexCruise] = [];
      cruise.flightPrices.forEach((detail: any, index: number) => {
        expandedDetail.value[indexCruise][index] = {
          Economy: typeProcess[indexCruise][index] === 'Economy',
          Business: typeProcess[indexCruise][index] === 'Business'
        };
      });
    });
  },
  { immediate: true, deep: true }
);
</script>
<template>
  <div class="cruise-worksheet">
    <div class="cruise-manual-modal">
      <section id="header" class="my-3 px-3">
        <div class="grid">
          <div class="col-6 text-lg font-bold">{{ $t('worksheet.groups_quote') }}</div>
          <div class="col-6 text-right text-lg font-bold">({{ $t('worksheet.cruise_manual') }})</div>
        </div>
      </section>
      <section id="information" class="px-3">
        <div class="grid">
          <div class="col-3">
            <div class="quote-id font-bold">
              <div class="grid">
                <div class="col-6 mb-4">{{ $t('worksheet.quote_id') }}:</div>
                <div class="col-6 font-normal">{{ quote?.sk }}</div>
              </div>
            </div>
            <LinkedQuotesEditor :worksheetData="quote" :permission="hasUserPermission(quote, canEditHeader)" @changeLinkedQuote="changeLinkedQuoteCruise($event)"></LinkedQuotesEditor>
          </div>
          <div class="col-4">
            <div class="date-quote font-bold">
              <div class="grid">
                <div class="col-4 mb-4">{{ $t('worksheet.date_quoted') }}:</div>
                <div class="col-8 font-normal">{{ formatDate(quote?.created, 'dd-LLL-yyyy', true, currentLanguage) }}</div>
              </div>
            </div>
            <div class="expiry-date font-bold">
              <div class="grid">
                <div class="col-4 mb-4">{{ $t('worksheet.expiry_date') }}:</div>
                <div class="col-8 font-normal">
                  <div v-if="!editExpiryDate.isEdit" class="flex align-items-top">
                    <div class="min-w-6rem">
                      {{ formatDate(quote?.expires, 'dd-LLL-yyyy', false, currentLanguage) }}
                    </div>
                    <span class="text-center ml-4">
                      <i v-if="hasUserPermission(quote, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editExpiryDate.isEdit = true"></i>
                    </span>
                  </div>
                  <div v-else class="flex align-items-top">
                    <div class="w-9rem">
                      <DatePicker id="calendar-24h" v-model="editExpiryDate.data" dateFormat="dd-M-yy">
                        <template #footer>
                          <p class="text-center">
                            <small>{{ $t('common.press_space') }}</small>
                          </p>
                        </template>
                      </DatePicker>
                    </div>
                    <div class="text-center ml-4 flex gap-3 align-items-center">
                      <i class="pi pi-check cursor-pointer" v-if="editExpiryDate.data" @click="handleSaveExpiry(editExpiryDate.data)"></i>
                      <i class="pi pi-times cursor-pointer" @click="editExpiryDate = { isEdit: false, data: quote?.expires && new Date(quote?.expires) }"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="book-date font-bold">
              <div class="grid">
                <div class="col-4">{{ $t('worksheet.book_dated') }}:</div>
                <div class="col-8 font-normal">
                  <div v-if="!editDateBooked.isEdit" class="flex align-items-top">
                    <div class="min-w-6rem">
                      {{ formatDate(quote?.bookedDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                    </div>
                    <span class="text-center ml-4">
                      <i v-if="hasUserPermission(quote, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editDateBooked.isEdit = true"></i>
                    </span>
                  </div>
                  <div v-else class="flex align-items-top">
                    <div class="w-9rem">
                      <DatePicker id="calendar-24h" v-model="editDateBooked.data" dateFormat="dd-M-yy">
                        <template #footer>
                          <p class="text-center">
                            <small>{{ $t('common.press_space') }}</small>
                          </p>
                        </template>
                      </DatePicker>
                    </div>
                    <div class="text-center ml-4 flex gap-3 align-items-center">
                      <i class="pi pi-check cursor-pointer" @click="handleSaveDateBooked(editDateBooked.data)"></i>
                      <i class="pi pi-times cursor-pointer" @click="editDateBooked = { isEdit: false, data: quote?.bookedDate && new Date(quote?.bookedDate) }"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-5">
            <div class="quote-assigned-to font-bold">
              <div class="grid">
                <div class="col-4 mb-4">{{ $t('worksheet.quote_assigned_to') }}:</div>
                <div class="col-8 font-normal">{{ quote?.assignedTo === UNASSIGNED ? $t('status.unassigned') : quote?.assignedTo }}</div>
              </div>
            </div>

            <div class="book-by font-bold">
              <div class="grid">
                <div class="col-4">{{ $t('worksheet.book_by') }}:</div>
                <div class="col-8 font-normal flex gap-1">
                  <div>{{ quote?.user }}</div>
                  <div>{{ quote?.email && `(${quote?.email})` }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="details">
        <Tabs :value="0">
          <TabList>
            <Tab v-for="(_, index) in quote?.cruises" :key="index" :value="index">{{ $t('common.option') }} {{ index + 1 }}</Tab>
            <Tab v-if="hasUserPermission(quote, canViewTermConditions)" :key="quote?.cruises.length" :value="quote?.cruises.length">{{ $t('menu.term_and_condition') }}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel v-for="(cruise, cruiseIndex) in quote?.cruises" :key="cruiseIndex" :value="cruiseIndex">
              <div class="flight-option" v-if="cruise.flights && cruise.flights.length && hasUserPermission(quote, canViewFlight)">
                <div>
                  <div class="option">
                    <div class="lead-option mb-2">{{ `${$t('common.flight')}` }}</div>
                    <div class="detail-option flex justify-content-between flex-wrap lg:flex-nowrap pr-4">
                      <div>
                        <div class="itinerary font-bold mb-2 mt-2">
                          <div class="flex gap-3">
                            <div class="min-w-label">{{ $t('advance_quote.form.itinerary') }}:</div>
                            <div class="font-normal">{{ $t(`advance_quote.form.${cruise.itinerary.toLowerCase()}`) }}</div>
                          </div>
                        </div>
                        <div class="gateway font-bold mb-2 mt-2">
                          <div class="flex gap-3">
                            <div class="min-w-label">{{ $t('common.gateway') }}:</div>
                            <div class="font-normal">{{ getTextVariant(currentLanguage, listOutbound[cruiseIndex]?.[0]?.gate, listOutbound[cruiseIndex]?.[0]?.gateFr) }}</div>
                          </div>
                        </div>
                        <div class="destination font-bold mb-2">
                          <div class="flex gap-3">
                            <div class="min-w-label">{{ $t('common.destination') }}:</div>
                            <div class="font-normal">{{ getTextVariant(currentLanguage, listOutbound[cruiseIndex]?.[listOutbound[cruiseIndex]?.length - 1]?.dest, listOutbound[cruiseIndex]?.[listOutbound[cruiseIndex]?.length - 1]?.destFr) }}</div>
                          </div>
                        </div>
                        <div class="returnFrom font-bold mb-2" v-if="cruise.itinerary === OPENJAW">
                          <div class="flex gap-3">
                            <div class="min-w-label">{{ $t('advance_quote.form.returning_from') }}:</div>
                            <div class="font-normal">{{ getTextVariant(currentLanguage, cruise.returnFrom, cruise.returnFromFr) }} ({{ cruise.returnFromCode }})</div>
                          </div>
                        </div>
                        <div class="returnTo font-bold mb-2" v-if="cruise.itinerary === OPENJAW">
                          <div class="flex gap-3">
                            <div class="min-w-label">{{ $t('advance_quote.form.returning_to') }}:</div>
                            <div class="font-normal">{{ getTextVariant(currentLanguage, cruise.returnTo, cruise.returnToFr) }} ({{ cruise.returnToCode }})</div>
                          </div>
                        </div>
                        <div class="hotel font-bold mb-2">
                          <div class="flex gap-3">
                            <div class="min-w-label">{{ $t('worksheet.dep_date') }}:</div>
                            <div class="font-normal">{{ formatDate(listOutbound[cruiseIndex]?.[0]?.depDate, 'dd-LLL-yyyy', false, currentLanguage) }}</div>
                          </div>
                        </div>
                        <div class="hotel font-bold mb-2" v-if="cruise.itinerary !== ONEWAY">
                          <div class="flex gap-3">
                            <div class="min-w-label">{{ $t('worksheet.return_date') }}:</div>
                            <div class="font-normal">{{ formatDate(listInbound[cruiseIndex]?.[0]?.depDate, 'dd-LLL-yyyy', false, currentLanguage) }}</div>
                          </div>
                        </div>
                      </div>
                      <div class="flex flex-column align-items-end">
                        <div class="hotel-room-cat font-bold mb-2 mt-2">
                          <div class="flex">
                            <div class="min-w-label">{{ $t('common.class') }}:</div>
                            <div class="font-normal w-6rem">{{ $t('worksheet.economy') }}</div>
                          </div>
                        </div>
                        <div class="adults font-bold mb-2">
                          <div class="flex" v-if="!editAdultFlight.isEdit">
                            <span class="text-center mr-4">
                              <i v-if="hasUserPermission(quote, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editAdultFlight = { isEdit: true, data: cruise.flights[0].adt }"></i>
                            </span>
                            <div class="min-w-label">{{ $t('common.adults') }}:</div>
                            <div class="font-normal w-6rem">{{ cruise.flights[0].adt }}</div>
                          </div>
                          <div class="flex" v-else>
                            <div class="min-w-label">{{ $t('common.adults') }}:</div>
                            <div class="w-3rem">
                              <InputNumber v-model="editAdultFlight.data" :min="0" />
                            </div>
                            <div class="text-center ml-2 flex gap-2 align-items-center">
                              <i class="pi pi-check cursor-pointer" @click="handleSaveAdult(editAdultFlight.data, cruise.flights[0], 'adt', editAdultFlight, cruise.flights)"></i>
                              <i class="pi pi-times cursor-pointer" @click="editAdultFlight = { isEdit: false, data: cruise.flights[0].adt }"></i>
                            </div>
                          </div>
                        </div>
                        <div class="child font-bold mb-2">
                          <div class="flex" v-if="!editChildFlight.isEdit">
                            <span class="text-center mr-4">
                              <i v-if="hasUserPermission(quote, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editChildFlight = { isEdit: true, data: cruise.flights[0].chd }"></i>
                            </span>
                            <div class="min-w-label">{{ $t('common.children') }}:</div>
                            <div class="font-normal w-6rem">{{ cruise.flights[0].chd }}</div>
                          </div>
                          <div class="flex" v-else>
                            <div class="min-w-label">{{ $t('common.children') }}:</div>
                            <div class="w-3rem">
                              <InputNumber v-model="editChildFlight.data" :min="0" />
                            </div>
                            <div class="text-center ml-2 flex gap-2 align-items-center">
                              <i class="pi pi-check cursor-pointer" @click="handleSaveChild(editChildFlight.data, cruise.flights[0], 'chd', editChildFlight, cruise.flights)"></i>
                              <i class="pi pi-times cursor-pointer" @click="editChildFlight = { isEdit: false, data: cruise.flights[0].chd }"></i>
                            </div>
                          </div>
                        </div>
                        <div class="total font-bold mb-2">
                          <div class="flex">
                            <div class="min-w-label">{{ $t('worksheet.total_passengers') }}:</div>
                            <div class="font-normal w-6rem">{{ cruise.flights[0].adt + cruise.flights[0].chd }}</div>
                          </div>
                        </div>
                      </div>
                      <div class="flex flex-column align-items-end">
                        <div class="hotel-room-cat font-bold mb-2 mt-2">
                          <div class="flex">
                            <div class="min-w-label">{{ $t('common.class') }}:</div>
                            <div class="font-normal w-6rem">{{ $t('worksheet.business') }}</div>
                          </div>
                        </div>
                        <div class="adults font-bold mb-2">
                          <div class="flex" v-if="!editAdultBusinessFlight.isEdit">
                            <span class="text-center mr-4">
                              <i v-if="hasUserPermission(quote, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editAdultBusinessFlight = { isEdit: true, data: cruise.flights[0].businessAdt }"></i>
                            </span>
                            <div class="min-w-label">{{ $t('common.adults') }}:</div>
                            <div class="font-normal w-6rem">{{ cruise.flights[0].businessAdt }}</div>
                          </div>
                          <div class="flex" v-else>
                            <div class="min-w-label">{{ $t('common.adults') }}:</div>
                            <div class="w-3rem">
                              <InputNumber v-model="editAdultBusinessFlight.data" :min="0" />
                            </div>
                            <div class="text-center ml-2 flex gap-2 align-items-center">
                              <i class="pi pi-check cursor-pointer" @click="handleSaveAdult(editAdultBusinessFlight.data, cruise.flights[0], 'businessAdt', editAdultBusinessFlight, cruise.flights)"></i>
                              <i class="pi pi-times cursor-pointer" @click="editAdultBusinessFlight = { isEdit: false, data: cruise.flights[0].businessAdt }"></i>
                            </div>
                          </div>
                        </div>
                        <div class="child font-bold mb-2">
                          <div class="flex" v-if="!editChildBusinessFlight.isEdit">
                            <span class="text-center mr-4">
                              <i v-if="hasUserPermission(quote, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editChildBusinessFlight = { isEdit: true, data: cruise.flights[0].businessChd }"></i>
                            </span>
                            <div class="min-w-label">{{ $t('common.children') }}:</div>
                            <div class="font-normal w-6rem">{{ cruise.flights[0].businessChd }}</div>
                          </div>
                          <div class="flex" v-else>
                            <div class="min-w-label">{{ $t('common.children') }}:</div>
                            <div class="w-3rem">
                              <InputNumber v-model="editChildBusinessFlight.data" :min="0" />
                            </div>
                            <div class="text-center ml-2 flex gap-2 align-items-center">
                              <i class="pi pi-check cursor-pointer" @click="handleSaveChild(editChildBusinessFlight.data, cruise.flights[0], 'businessChd', editChildBusinessFlight, cruise.flights)"></i>
                              <i class="pi pi-times cursor-pointer" @click="editChildBusinessFlight = { isEdit: false, data: cruise.flights[0].businessChd }"></i>
                            </div>
                          </div>
                        </div>
                        <div class="total font-bold mb-2">
                          <div class="flex">
                            <div class="min-w-label">{{ $t('worksheet.total_passengers') }}:</div>
                            <div class="font-normal w-6rem">{{ cruise.flights[0].businessAdt + cruise.flights[0].businessChd }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="flight-detail" v-if="cruise.flights.length > 0 && hasUserPermission(quote, canViewFlight)">
                    <div class="lead-flight flex align-items-center justify-content-between">
                      <span> {{ $t('worksheet.flight_details') }} </span>
                      <Button size="small" id="view_avail_btn" severity="danger" class="custom-buttom action-button" :label="$t('common.buttons.view_availability')" @click="handleOpenViewFlightSeat(cruise, cruiseIndex)" :loading="isLoadingButton" />
                    </div>
                    <div class="detail">
                      <DataTable
                        v-model:editingRows="editingRows"
                        :value="filterFlightFollowType(cruise.flights, cruise.itinerary)"
                        rowGroupMode="subheader"
                        groupRowsBy="groupBy"
                        class="w-full"
                        showGridlines
                        editMode="row"
                        tableClass="flight-detail"
                        @row-edit-save="onRowEditSave($event, cruise.flights)"
                      >
                        <template #groupheader="slotProps">
                          <div class="flex align-items-center justify-content-end gap-2">
                            <div class="add-flight-button" @click="addNewFlight(cruise, slotProps.data.type)">
                              <span v-if="cruise.itinerary === ONEWAY">+ {{ $t('management.airline.add_flight') }}</span>
                              <span v-else>+ {{ $t(`worksheet.add_${slotProps?.data?.type?.toLowerCase()}`) }}</span>
                            </div>
                          </div>
                        </template>
                        <TableColumn field="groupBy"></TableColumn>
                        <TableColumn field="type" :header="$t('worksheet.type')" dataType="string" class="min-w-5rem">
                          <template #body="{ data }">
                            {{ $t(`worksheet.${data?.type?.toLowerCase()}`) }}
                          </template>
                        </TableColumn>
                        <TableColumn field="operated" :header="$t('worksheet.airline')" dataType="string" class="min-w-9rem">
                          <template #body="{ data }">
                            {{ data.airline }}
                          </template>

                          <template #editor="{ data, field }">
                            <Select v-model="data[field]" :options="listAirline" optionLabel="name" optionValue="code" :placeholder="$t('worksheet.select_type')" class="w-full" />
                          </template>
                        </TableColumn>
                        <TableColumn field="flightNumber" :header="$t('worksheet.flight_number')" dataType="string" class="min-w-10rem">
                          <template #body="{ data }">
                            {{ data.flightNumber }}
                          </template>

                          <template #editor="{ data, field }">
                            <InputText v-model="data[field]" :maxlength="5" @input="validateFlightNumber(data, field)" />
                          </template>
                        </TableColumn>
                        <TableColumn field="gate" :header="$t('worksheet.from')" dataType="string" class="min-w-10rem">
                          <template #body="{ data }">
                            {{ getTextVariant(currentLanguage, data.gate, data.gateFr) }}
                          </template>
                          <template #editor="{ data, field }">
                            <AutoComplete v-model="data[field]" :suggestions="filterCruiseGateway" @complete="searchCruiseGateway($event)" completeOnFocus optionLabel="selectedLabel" class="w-full" forceSelection>
                              <template #option="slotProps">
                                <div class="flex align-options-center">
                                  <div>
                                    <div>{{ slotProps.option.airportName }} ({{ slotProps.option.airportCode }})</div>
                                    <div class="text-sm">{{ slotProps.option.cityName }}, {{ slotProps.option.province || slotProps.option.region }}, {{ slotProps.option.countryName }}</div>
                                  </div>
                                </div>
                              </template>
                            </AutoComplete>
                          </template>
                        </TableColumn>
                        <TableColumn field="dest" :header="$t('worksheet.to')" dataType="string" class="min-w-10rem">
                          <template #body="{ data }">
                            {{ getTextVariant(currentLanguage, data.dest, data.destFr) }}
                          </template>

                          <template #editor="{ data, field }">
                            <AutoComplete v-model="data[field]" :suggestions="filterCruiseDestination" @complete="searchCruiseDestination($event)" completeOnFocus optionLabel="selectedLabel" class="w-full" forceSelection>
                              <template #option="slotProps">
                                <div class="flex align-options-center">
                                  <div>
                                    <div>{{ slotProps.option.airportName }} ({{ slotProps.option.airportCode }})</div>
                                    <div class="text-sm">{{ slotProps.option.cityName }}, {{ slotProps.option.province || slotProps.option.region }}, {{ slotProps.option.countryName }}</div>
                                  </div>
                                </div>
                              </template>
                            </AutoComplete>
                          </template>
                        </TableColumn>
                        <TableColumn field="depDate" :header="$t('worksheet.dep_date')" class="min-w-10rem">
                          <template #body="{ data }">
                            {{ formatDate(data?.depDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                          </template>
                          <template #editor="{ data, field }">
                            <DatePicker id="calendar-24h" v-model="data[field]" dateFormat="dd-M-yy">
                              <template #footer>
                                <p class="text-center">
                                  <small>{{ $t('common.press_space') }}</small>
                                </p>
                              </template>
                            </DatePicker>
                          </template>
                        </TableColumn>
                        <TableColumn field="depTime" :header="$t('worksheet.dep_time')" dataType="date" class="min-w-8rem">
                          <template #body="{ data }">
                            {{ data.depTime }}
                          </template>
                          <template #editor="{ data, field }">
                            <div class="dep-time-class">
                              <DatePicker id="calendar-24h" showTime hourFormat="24" v-model="data[field]" timeOnly>
                                <template #footer>
                                  <p class="text-center">
                                    <small>{{ $t('common.press_space') }}</small>
                                  </p>
                                </template>
                              </DatePicker>
                            </div>
                          </template>
                        </TableColumn>
                        <TableColumn field="arrDate" :header="$t('worksheet.arrival_date')" class="min-w-10rem">
                          <template #body="{ data }">
                            {{ formatDate(data?.arrDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                          </template>
                          <template #editor="{ data, field }">
                            <DatePicker id="calendar-24h" v-model="data[field]" dateFormat="dd-M-yy">
                              <template #footer>
                                <p class="text-center">
                                  <small>{{ $t('common.press_space') }}</small>
                                </p>
                              </template>
                            </DatePicker>
                          </template>
                        </TableColumn>
                        <TableColumn field="arrTime" :header="$t('worksheet.arrival_time')" dataType="date" class="min-w-8rem">
                          <template #body="{ data }">
                            {{ data.arrTime }}
                          </template>

                          <template #editor="{ data, field }">
                            <div class="arr-time-class">
                              <DatePicker id="calendar-24h" showTime hourFormat="24" v-model="data[field]" timeOnly>
                                <template #footer>
                                  <p class="text-center">
                                    <small>{{ $t('common.press_space') }}</small>
                                  </p>
                                </template>
                              </DatePicker>
                            </div>
                          </template>
                        </TableColumn>
                        <TableColumn field="via" :header="$t('worksheet.via')" dataType="string" class="w-5rem min-w-5rem">
                          <template #body="{ data }">
                            {{ data.via }}
                          </template>

                          <template #editor="{ data, field }">
                            <InputText v-model="data[field]" :maxlength="3" />
                          </template>
                        </TableColumn>
                        <TableColumn field="fClass" :header="$t('common.class')" dataType="string" class="w-5rem">
                          <template #body="{ data }">
                            <span>{{ data.fClass }}</span>
                          </template>
                          <template #editor="{ data, field }">
                            <InputText v-model="data[field]" :maxlength="5" />
                          </template>
                        </TableColumn>
                        <TableColumn field="cabin" :header="$t('common.cabin')" dataType="string" class="word-break w-10rem max-w-10rem">
                          <template #body="{ data, field }">
                            {{ getCabinName(data[field]) }}
                          </template>

                          <template #editor="{ data, field }">
                            <Select v-model="data[field]" :options="LIST_CABIN" optionLabel="name" optionValue="data" :placeholder="$t('worksheet.select_type')" class="w-full" />
                          </template>
                        </TableColumn>
                        <TableColumn v-if="hasUserPermission(quote, canEditFlight)" :rowEditor="true" :header="$t('common.action')" class="text-center relative">
                          <template #body="{ editorInitCallback, data }">
                            <div class="flex flex-column">
                              <i class="pi pi-pencil table-action-button" @click="editorInitCallback()"></i>
                              <i v-if="data.canDelete" class="pi pi-times-circle table-action-button text-primary" @click="deleteFlight(cruise, data)"></i>
                            </div>
                          </template>
                          <template #editor="{ editorSaveCallback, editorCancelCallback }">
                            <div class="flex flex-column">
                              <i class="pi pi-check table-action-button" @click="editorSaveCallback()"></i>
                              <i class="pi pi-times table-action-button" @click="editorCancelCallback()"></i>
                            </div>
                          </template>
                        </TableColumn>
                      </DataTable>
                    </div>
                  </div>
                </div>
                <div v-if="cruise.flightPrices.length">
                  <div class="flight-pricing pricing" v-for="(pricing, index) in formatFlightPrice(cruise.flightPrices, authorizations?.worksheet_cruise)" :key="index">
                    <div class="lead-pricing flex justify-content-between cursor-pointer" @click="expandCruiseFlightPricing(pricing.type, cruiseIndex, index)">
                      <div class="flex align-items-center">
                        <div>{{ formatHeaderPricing(cruise, pricing, index) }}</div>
                        <div class="flex align-items-center ml-2">[&nbsp; <i class="pi pi-minus" v-if="expandedDetail[cruiseIndex][index][pricing.type]"></i> <i class="pi pi-plus" v-else></i>&nbsp;]</div>
                      </div>
                      <Button
                        v-if="isProcess(cruise, pricing.type, index) && hasUserPermission(quote, authorizations?.worksheet_cruise?.search_flight_edit)"
                        size="small"
                        @click.stop="openFlightModal(cruise, cruiseIndex, pricing, index, quote)"
                        severity="danger"
                        class="custom-buttom action-button"
                        :label="$t('common.buttons.process')"
                      ></Button>
                    </div>
                    <div v-show="expandedDetail[cruiseIndex][index][pricing.type]">
                      <div class="detail-pricing">
                        <DataTable v-model:editingRows="editingRows[index + 1]" :value="pricing.rows" class="w-full" showGridlines editMode="row" @row-edit-save="changePricingFlight($event, cruise.flightPrices[index], pricing.rows)">
                          <TableColumn field="rowName" dataType="string" class="min-w-10rem h-3rem font-bold relative">
                            <template #body="{ data, field }">
                              <div :class="[data.key === 'total' ? 'font-bold text-900 p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                                {{ data[field] && $t(data[field]) }} <i v-if="data.rowName === 'worksheet.agency_taxes'" class="pi pi-plus-circle ml-1 mt-1 cursor-pointer" @click="openPriceBreakModal(cruise.flightPrices[index])"></i>
                              </div>
                            </template>
                          </TableColumn>
                          <TableColumn field="fare" :header="$t('worksheet.fare_type')" dataType="number" class="min-w-10rem h-3rem relative">
                            <template #editor="{ data, field, index }">
                              <InputNumber v-if="index !== 0 && index !== 1" :minFractionDigits="2" v-model="data[field]" />
                              <div v-else :class="[data.key === 'total' ? 'font-bold text-900 p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                                <div>{{ data[field] ? formatPrice(Number(data[field])) : '0.00' }}</div>
                              </div>
                            </template>
                            <template #body="{ data, field }">
                              <div :class="[data.key === 'total' ? 'font-bold p-3 absolute text-900 top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                                <div>{{ data[field] ? formatPrice(Number(data[field])) : '0.00' }}</div>
                              </div>
                            </template>
                          </TableColumn>
                          <TableColumn field="adt" :header="$t('worksheet.12_and_up')" dataType="number" class="min-w-10rem h-3rem relative">
                            <template #editor="{ data, field, index }">
                              <InputNumber v-if="index === 0 || index === 1" :minFractionDigits="2" v-model="data[field]" />
                            </template>
                            <template #body="{ data, field }">
                              <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                                <div>{{ data[field] ? formatPrice(Number(data[field])) : '' }}</div>
                              </div>
                            </template>
                          </TableColumn>
                          <TableColumn field="chd" :header="$t('common.child_2_11')" dataType="number" class="min-w-10rem h-3rem relative">
                            <template #editor="{ data, field, index }">
                              <InputNumber v-if="index === 0 || index === 1" :minFractionDigits="2" v-model="data[field]" />
                            </template>
                            <template #body="{ data, field }">
                              <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                                <div>{{ data[field] ? formatPrice(Number(data[field])) : '' }}</div>
                              </div>
                            </template>
                          </TableColumn>
                          <TableColumn field="inf" :header="$t('common.infant_0_1')" dataType="number" class="min-w-10rem h-3rem relative">
                            <template #editor="{ data, field, index }">
                              <InputNumber v-if="index === 0 || index === 1" :minFractionDigits="2" v-model="data[field]" />
                            </template>
                            <template #body="{ data, field }">
                              <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                                <div>{{ data[field] ? formatPrice(Number(data[field])) : '' }}</div>
                              </div>
                            </template>
                          </TableColumn>
                          <TableColumn field="detail" :header="$t('worksheet.baggage_details')" dataType="number" class="break-word min-w-10rem h-3rem relative">
                            <template #editor="{ data, field, index }">
                              <InputText v-if="index === 0" v-model="data[field]" />
                            </template>
                            <template #body="{ data, field, index }">
                              <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                                <p v-if="index == 0">{{ data[field] }}</p>
                              </div>
                            </template>
                          </TableColumn>
                          <TableColumn v-if="hasUserPermission(quote, true)" :header="$t('common.action')" :rowEditor="true" class="w-1 text-center h-3rem relative">
                            <template #body="{ editorInitCallback, data }">
                              <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                                <i v-if="data.key !== 'total' && data.canEdit" class="pi pi-pencil" @click="editorInitCallback"></i>
                              </div>
                              <p v-if="data.key === 'total'" class="opacity-0"></p>
                            </template>
                            <template #editor="{ editorSaveCallback, editorCancelCallback }">
                              <div class="flex">
                                <i class="pi pi-check edit-pi" @click="editorSaveCallback"></i>
                                <i class="pi pi-times edit-pi" @click="editorCancelCallback"></i>
                              </div>
                            </template>
                          </TableColumn>
                        </DataTable>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="promotions" v-if="hasUserPermission(quote, canViewPromotion)">
                  <div class="promotions">
                    <div class="lead-promotions">{{ $t('worksheet.promotions') }}</div>
                    <div v-if="promoEditedData.index !== `flight${cruiseIndex}`" class="flex justify-content-between">
                      <div class="w-full">
                        <HTMLEditor :readonly="true" v-model="cruise.promoFlight" class="custom-editor" />
                      </div>
                      <div class="flex text-center">
                        <i v-if="hasUserPermission(quote, canEditPromotion)" class="pi pi-pencil edit-pi cursor-pointer" @click="promoEditedData = { index: `flight${cruiseIndex}`, data: cruise.promoFlight }"></i>
                      </div>
                    </div>
                    <div v-else class="flex justify-content-between">
                      <div class="w-full">
                        <HTMLEditor v-model="promoEditedData.data" class="custom-editor" />
                      </div>
                      <div class="text-center flex flex-col">
                        <i class="pi pi-check edit-pi cursor-pointer" @click="handleSavePromo(cruise, 'promoFlight')"></i>
                        <i class="pi pi-times edit-pi cursor-pointer" @click="promoEditedData.index = null"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="agency-comment" v-if="hasUserPermission(quote, canViewAgencyComment)">
                  <div class="lead-comment">{{ $t('worksheet.agency_comment') }}</div>
                  <div v-if="editComment.index !== `flight${cruiseIndex}`" class="flex">
                    <div class="w-full">
                      <HTMLEditor :readonly="true" v-model="cruise.commentFlight" class="custom-editor" />
                    </div>
                    <div class="text-center">
                      <i v-if="hasUserPermission(quote, canEditAgencyComment)" class="pi pi-pencil edit-pi cursor-pointer" @click="editComment = { index: `flight${cruiseIndex}`, data: cruise.commentFlight }"></i>
                    </div>
                  </div>
                  <div v-else class="flex">
                    <div class="w-full">
                      <HTMLEditor v-model="editComment.data" class="custom-editor" />
                    </div>
                    <div class="text-center flex flex-col">
                      <i class="pi pi-check edit-pi cursor-pointer" @click="saveComment(cruise, 'commentFlight')"></i>
                      <i class="pi pi-times edit-pi cursor-pointer" @click="editComment.index = null"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div class="cruise-option option">
                <div class="lead-option">{{ `${$t('worksheet.cruise')}` }}</div>
                <div class="detail-option">
                  <div class="gateway font-bold flex justify-content-between flex-wrap lg:flex-nowrap">
                    <div>
                      <div class="flex gap-3 mt-2 mb-2">
                        <div class="min-w-label">{{ $t('worksheet.cruise_line') }}:</div>
                        <div class="font-normal">{{ cruise.cruiseLine }}</div>
                      </div>
                      <div class="flex gap-3 mb-2">
                        <div class="min-w-label">{{ $t('worksheet.cruise_ship') }}:</div>
                        <div class="font-normal">{{ cruise.ship }}</div>
                      </div>
                      <div class="hotel-room-cat font-bold">
                        <div class="flex gap-3 mb-2">
                          <div class="min-w-label">{{ $t('worksheet.cruise_sailing') }}:</div>
                          <div class="font-normal">{{ cruise.sailing }}</div>
                        </div>
                      </div>
                      <div class="adults font-bold">
                        <div class="flex gap-3 mb-2">
                          <div class="min-w-label">{{ $t('common.category') }}:</div>
                          <div class="font-normal">{{ formatCruiseCategory(cruise.cabinCategory) }}</div>
                        </div>
                      </div>
                      <div class="child font-bold">
                        <div class="flex gap-3 mb-2">
                          <div class="min-w-label">{{ $t('worksheet.additional_product') }}:</div>
                          <div class="font-normal">{{ cruise.additionalNote }}</div>
                        </div>
                      </div>
                      <div class="child font-bold">
                        <div class="flex gap-3 mb-2">
                          <div class="min-w-label">{{ $t('worksheet.dinning') }}:</div>
                          <div class="font-normal">{{ cruise.dining }}</div>
                        </div>
                      </div>
                    </div>
                    <div class="flex flex-column align-items-end">
                      <div class="adults font-bold mb-2 mt-2">
                        <div class="flex" v-if="!editAdult.isEdit">
                          <span class="text-center mr-4">
                            <i v-if="hasUserPermission(quote, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editAdult = { isEdit: true, data: cruise.adt }"></i>
                          </span>
                          <div class="min-w-label">{{ $t('common.adults') }}:</div>
                          <div class="font-normal w-6rem">{{ cruise.adt }}</div>
                        </div>
                        <div class="flex" v-else>
                          <div class="min-w-label">{{ $t('common.adults') }}:</div>
                          <div class="w-3rem">
                            <InputNumber v-model="editAdult.data" :min="0" />
                          </div>
                          <div class="text-center ml-2 flex gap-2 align-items-center">
                            <i class="pi pi-check cursor-pointer" @click="handleSaveAdult(editAdult.data, cruise, 'adt', editAdult)"></i>
                            <i class="pi pi-times cursor-pointer" @click="editAdult = { isEdit: false, data: cruise.adt }"></i>
                          </div>
                        </div>
                      </div>
                      <div class="child font-bold mb-2">
                        <div class="flex" v-if="!editChild.isEdit">
                          <span class="text-center mr-4">
                            <i v-if="hasUserPermission(quote, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editChild = { isEdit: true, data: cruise.chd }"></i>
                          </span>
                          <div class="min-w-label">{{ $t('common.children') }}:</div>
                          <div class="font-normal w-6rem">{{ cruise.chd }}</div>
                        </div>
                        <div class="flex" v-else>
                          <div class="min-w-label">{{ $t('common.children') }}:</div>
                          <div class="w-3rem">
                            <InputNumber v-model="editChild.data" :min="0" />
                          </div>
                          <div class="text-center ml-2 flex gap-2 align-items-center">
                            <i class="pi pi-check cursor-pointer" @click="handleSaveChild(editChild.data, cruise, 'chd', editChild)"></i>
                            <i class="pi pi-times cursor-pointer" @click="editChild = { isEdit: false, data: cruise.chd }"></i>
                          </div>
                        </div>
                      </div>

                      <div class="total font-bold">
                        <div class="flex mb-2">
                          <div class="min-w-label">{{ $t('worksheet.total_passengers') }}:</div>
                          <div class="font-normal w-6rem">
                            {{ cruise.adt + cruise.chd }}
                          </div>
                        </div>
                      </div>
                      <div class="total font-bold">
                        <div class="flex mb-2">
                          <div class="min-w-label"># {{ $t('worksheet.cabins') }}:</div>
                          <div class="font-normal w-6rem">{{ cruise.cabinNo }}</div>
                        </div>
                      </div>
                      <div class="duration font-bold">
                        <div class="flex mb-2">
                          <div class="min-w-label">{{ $t('worksheet.duration') }}:</div>
                          <div class="font-normal w-6rem">{{ cruise.duration }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="cruise-pricing pricing mb-3" v-for="(cabin, cabinIndex) in formatCruiseData(cruise.cabins, authorizations?.worksheet_cruise)" :key="cabinIndex">
                    <div class="lead-pricing">{{ `${$t('common.pricing')} (${formatPricingHead(cruise.cabinCategory, cabin.code)})` }}</div>
                    <div class="detail-pricing">
                      <DataTable v-model:editingRows="cabinEditingRows[cabinIndex]" :value="cabin.cabinRows" class="w-full" showGridlines editMode="row" @row-edit-save="changePricingCabinCruise($event, cruise.cabins[cabinIndex])">
                        <TableColumn field="label" dataType="string" class="font-bold relative">
                          <template #body="{ data, field }">
                            <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']" class="min-w-7rem">
                              {{ data[field] && $t(data[field]) }}
                            </div>
                          </template>
                        </TableColumn>
                        <TableColumn field="singlePrice" :header="$t('book_form.single')" dataType="string" class="relative">
                          <template #editor="{ data, field, index }">
                            <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                          </template>
                          <template #body="{ data, field, index }">
                            <div v-if="index !== 0" :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                              {{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}
                            </div>
                          </template>
                        </TableColumn>
                        <TableColumn field="doublePrice" :header="$t('book_form.double')" dataType="string" class="relative">
                          <template #editor="{ data, field, index }">
                            <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                          </template>
                          <template #body="{ data, field, index }">
                            <div v-if="index !== 0" :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                              {{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}
                            </div>
                          </template>
                        </TableColumn>
                        <TableColumn field="triplePrice" :header="$t('book_form.triple')" dataType="string" class="relative">
                          <template #editor="{ data, field, index }">
                            <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                          </template>
                          <template #body="{ data, field, index }">
                            <div v-if="index !== 0" :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                              {{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}
                            </div>
                          </template>
                        </TableColumn>
                        <TableColumn field="quadPrice" :header="$t('book_form.quad')" dataType="string" class="relative">
                          <template #editor="{ data, field, index }">
                            <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                          </template>
                          <template #body="{ data, field, index }">
                            <div v-if="index !== 0" :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                              {{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}
                            </div>
                          </template>
                        </TableColumn>
                        <TableColumn field="fiveInRoomPrice" :header="$t('worksheet.five')" dataType="string" class="relative">
                          <template #editor="{ data, field, index }">
                            <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                          </template>
                          <template #body="{ data, field, index }">
                            <div v-if="index !== 0" :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                              {{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}
                            </div>
                          </template>
                        </TableColumn>
                        <TableColumn field="sixInRoomPrice" :header="$t('worksheet.six')" dataType="string" class="relative">
                          <template #editor="{ data, field, index }">
                            <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                          </template>
                          <template #body="{ data, field, index }">
                            <div v-if="index !== 0" :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                              {{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}
                            </div>
                          </template>
                        </TableColumn>
                        <TableColumn field="childOnePrice" :header="$t('common.child')" class="min-w-8rem relative">
                          <template #body="{ data, field, index }">
                            <div v-if="index === 0">
                              {{ data[field] }}
                            </div>
                            <div v-else :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                              <span>{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                            </div>
                          </template>
                          <template #editor="{ data, field, index }">
                            <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                            <InputText v-else v-model="data[field]" @input="handleChildCruiseInput(data[field], field)" class="w-full" />
                          </template>
                        </TableColumn>
                        <TableColumn field="childTwoPrice" :header="$t('common.child')" class="min-w-8rem relative">
                          <template #body="{ data, field, index }">
                            <div v-if="index === 0">
                              {{ data[field] }}
                            </div>
                            <div v-else :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                              <span>{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                            </div>
                          </template>
                          <template #editor="{ data, field, index }">
                            <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                            <InputText v-else v-model="data[field]" @input="handleChildCruiseInput(data[field], field)" class="w-full" />
                          </template>
                        </TableColumn>
                        <TableColumn field="childThreePrice" :header="$t('common.child')" class="min-w-8rem relative">
                          <template #body="{ data, field, index }">
                            <div v-if="index === 0">
                              {{ data[field] }}
                            </div>
                            <div v-else :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']" s>
                              <span>{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                            </div>
                          </template>
                          <template #editor="{ data, field, index }">
                            <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                            <InputText v-else v-model="data[field]" @input="handleChildCruiseInput(data[field], field)" class="w-full" />
                          </template>
                        </TableColumn>
                        <TableColumn field="infantPrice" :header="$t('common.infant')" dataType="number" class="relative">
                          <template #body="{ data, field, index }">
                            <div v-if="index === 0">
                              {{ data[field] }}
                            </div>
                            <div v-else :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                              <span>{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                            </div>
                          </template>
                          <template #editor="{ data, field, index }">
                            <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                            <InputText v-else v-model="data[field]" class="w-full" />
                          </template>
                        </TableColumn>
                        <TableColumn :rowEditor="true" :header="$t('common.action')" class="w-1 text-center relative">
                          <template #body="{ editorInitCallback, data }">
                            <i v-if="data.key !== 'total' && data.canEdit" class="pi pi-pencil" @click="editorInitCallback"></i>
                            <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']"></div>
                            <i v-if="data.key === 'total'" class="pi pi-pencil opacity-0"></i>
                          </template>
                          <template #editor="{ data, editorSaveCallback, editorCancelCallback }">
                            <i class="pi pi-check edit-pi" @click="handleSaveChildCruiseInput(editorSaveCallback, data)"></i>
                            <i class="pi pi-times edit-pi" @click="handleCancleChildInput(editorCancelCallback)"></i>
                          </template>
                        </TableColumn>
                      </DataTable>
                    </div>
                  </div>
                  <div class="promotions" v-if="hasUserPermission(quote, canViewPromotion)">
                    <div class="lead-comment">{{ $t('worksheet.promotions') }}</div>
                    <div v-if="promoEditedData.index !== `${cruiseIndex}Cruise`" class="flex">
                      <div class="w-full">
                        <HTMLEditor :readonly="true" v-model="cruise.promoCruise" class="custom-editor" />
                      </div>
                      <span class="text-center">
                        <i v-if="hasUserPermission(quote, canEditPromotion)" class="pi pi-pencil edit-pi cursor-pointer" @click="promoEditedData = { index: `${cruiseIndex}Cruise`, data: cruise.promoCruise }"></i>
                      </span>
                    </div>
                    <div v-else class="flex">
                      <div class="w-full">
                        <HTMLEditor v-model="promoEditedData.data" class="custom-editor" />
                      </div>
                      <div class="text-center flex flex-col">
                        <i class="pi pi-check edit-pi cursor-pointer" @click="handleSavePromo(cruise, 'promoCruise')"></i>
                        <i class="pi pi-times edit-pi cursor-pointer" @click="promoEditedData.index = null"></i>
                      </div>
                    </div>
                  </div>
                  <div class="agency-comment" v-if="hasUserPermission(quote, canViewAgencyComment)">
                    <div class="lead-comment">{{ $t('worksheet.agency_comment') }}</div>
                    <div v-if="editComment.index !== `${cruiseIndex}Cruise`" class="flex">
                      <div class="w-full">
                        <HTMLEditor :readonly="true" v-model="cruise.commentCruise" class="custom-editor" />
                      </div>
                      <span class="text-center">
                        <i v-if="hasUserPermission(quote, canViewAgencyComment)" class="pi pi-pencil edit-pi cursor-pointer" @click="editComment = { index: `${cruiseIndex}Cruise`, data: cruise.commentCruise }"></i>
                      </span>
                    </div>
                    <div v-else class="flex">
                      <div class="w-full">
                        <HTMLEditor v-model="editComment.data" class="custom-editor" />
                      </div>
                      <div class="text-center flex flex-col">
                        <i class="pi pi-check edit-pi cursor-pointer" @click="saveComment(cruise, 'commentCruise')"></i>
                        <i class="pi pi-times edit-pi cursor-pointer" @click="editComment.index = null"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- pre hotel -->
              <div id="pre-hotel" v-if="hasUserPermission(quote, authorizations?.worksheet_cruise?.pre_hotel_view)">
                <div v-for="(preItem, indexPre) in cruise.preHotel" :key="indexPre">
                  <div>
                    <HotelInform
                      :optionData="preItem"
                      :permission="canEditHeader"
                      :hotelWorkSheet="quote"
                      v-model:editAdult="editAdultPreHotel"
                      v-model:editChild="editChildPreHotel"
                      v-model:editNumberOfRoom="editNumberOfRoomPreHotel"
                      @handleSaveAdult="(adult) => handleSaveAdult(adult, preItem, 'adt', editAdultPreHotel)"
                      @handleSaveChild="(child) => handleSaveChild(child, preItem, 'chd', editChildPreHotel)"
                      @handleSaveNumberOfRoom="(numOfRoom) => handleSaveNumberOfRoom(numOfRoom, preItem, 'numOfRooms', editNumberOfRoomPreHotel)"
                      :isCruise="true"
                      :hotelTitle="$t('create_quote.pre_hotel')"
                    />
                  </div>
                  <div v-for="(preHotelItem, indexPreHotel) in preItem.hotels" :key="indexPreHotel">
                    <div>
                      <div class="lead-option">{{ $t('worksheet.hotel') }}</div>
                      <div class="detail-option flex justify-content-between flex-wrap pr-4 lg:flex-nowrap">
                        <div>
                          <div class="destination font-bold mb-2 mt-2">
                            <div class="flex gap-3">
                              <div class="min-w-label">{{ $t('common.destination') }}:</div>
                              <div class="font-normal">{{ preItem.airportCode }} ({{ preItem.dest || '' }})</div>
                            </div>
                          </div>
                          <div class="hotel font-bold mb-2">
                            <div class="flex gap-3">
                              <div class="min-w-label">{{ $t('worksheet.hotel') }}:</div>
                              <div class="font-normal">{{ preHotelItem.hotel || '' }}</div>
                            </div>
                          </div>
                          <div class="hotel-room-cat font-bold mb-2 flex-1 mt-2">
                            <div class="flex gap-3">
                              <div class="min-w-label">{{ $t('worksheet.hotel_room_category') }}:</div>
                              <div class="font-normal max-w-70 white-space-pre-line">{{ getRoomsName(preHotelItem) }}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="pricing" v-if="hasUserPermission(quote, authorizations?.worksheet_cruise?.pre_hotel_pricing_view)">
                        <div class="option" v-for="(room, index) in preHotelItem?.rooms" :key="index">
                          <div class="lead-pricing">{{ $t('common.pricing') }} ({{ getTextVariant(currentLanguage, room.longName, room.longNameFr) }})</div>
                          <DataTable
                            v-model:editingRows="editingRowsHotelPre[cruiseIndex][indexPre][indexPreHotel][index]"
                            :value="getHotelPrice(room.price)"
                            class="w-full"
                            editMode="row"
                            showGridlines
                            @row-edit-save="changePriceHotel($event, room.price)"
                          >
                            <TableColumn header="" dataType="string">
                              <template #body="{ data }">
                                {{ data.name && $t(data.name) }}
                              </template>
                            </TableColumn>
                            <TableColumn field="singlePrice" :header="$t('book_form.single')">
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                              </template>
                              <template #body="{ data, field, index }">
                                <div v-if="index !== 0">
                                  {{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}
                                </div>
                              </template>
                            </TableColumn>
                            <TableColumn field="doublePrice" :header="$t('book_form.double')">
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                              </template>
                              <template #body="{ data, field, index }">
                                <div v-if="index !== 0">
                                  {{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}
                                </div>
                              </template>
                            </TableColumn>
                            <TableColumn field="triplePrice" :header="$t('book_form.triple')">
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                              </template>
                              <template #body="{ data, field, index }">
                                <div v-if="index !== 0">
                                  {{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}
                                </div>
                              </template>
                            </TableColumn>
                            <TableColumn field="quadPrice" :header="$t('book_form.quad')">
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                              </template>
                              <template #body="{ data, field, index }">
                                <div v-if="index !== 0">
                                  {{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}
                                </div>
                              </template>
                            </TableColumn>
                            <TableColumn field="fiveInRoomPrice" :header="$t('book_form.5_in_room')">
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                              </template>
                              <template #body="{ data, field, index }">
                                <div v-if="index !== 0">
                                  {{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}
                                </div>
                              </template>
                            </TableColumn>
                            <TableColumn field="sixInRoomPrice" :header="$t('book_form.6_in_room')">
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                              </template>
                              <template #body="{ data, field, index }">
                                <div v-if="index !== 0">
                                  {{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}
                                </div>
                              </template>
                            </TableColumn>
                            <TableColumn field="childOnePrice" :header="$t('common.child')">
                              <template #body="{ data, field, index }">
                                <div v-if="index === 0">
                                  {{ data[field] }}
                                </div>
                                <div v-else>
                                  <span>{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                                </div>
                              </template>
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                                <InputText v-else v-model="data[field]" class="w-full" @input="handleChildCruiseInput(data[field], field)" />
                              </template>
                            </TableColumn>
                            <TableColumn field="childTwoPrice" :header="$t('common.child')">
                              <template #body="{ data, field, index }">
                                <div v-if="index === 0">
                                  {{ data[field] }}
                                </div>
                                <div v-else>
                                  <span>{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                                </div>
                              </template>
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                                <InputText v-else v-model="data[field]" class="w-full" @input="handleChildCruiseInput(data[field], field)" />
                              </template>
                            </TableColumn>
                            <TableColumn field="childThreePrice" :header="$t('common.child')">
                              <template #body="{ data, field, index }">
                                <div v-if="index === 0">
                                  {{ data[field] }}
                                </div>
                                <div v-else>
                                  <span>{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                                </div>
                              </template>
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                                <InputText v-else v-model="data[field]" class="w-full" @input="handleChildCruiseInput(data[field], field)" />
                              </template>
                            </TableColumn>
                            <TableColumn field="infantPrice" :header="$t('common.infant')">
                              <template #body="{ data, field, index }">
                                <div v-if="index === 0">
                                  {{ data[field] }}
                                </div>
                                <div v-else>
                                  <span>{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                                </div>
                              </template>
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                                <InputText v-else v-model="data[field]" class="w-full" />
                              </template>
                            </TableColumn>
                            <TableColumn v-if="hasUserPermission(quote, authorizations?.worksheet_cruise?.pre_hotel_pricing_edit)" :header="$t('common.action')" :rowEditor="true" class="w-8rem text-center">
                              <template #body="{ editorInitCallback, data }">
                                <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                                  <i v-if="data.key !== 'total'" class="pi pi-pencil" @click="editorInitCallback"></i>
                                </div>
                                <p v-if="data.key === 'total'" class="opacity-0"></p>
                              </template>
                              <template #editor="{ data, editorSaveCallback, editorCancelCallback }">
                                <i class="pi pi-check edit-pi" @click="handleSaveChildInput(editorSaveCallback, data)"></i>
                                <i class="pi pi-times edit-pi" @click="handleCancleChildInput(editorCancelCallback)"></i>
                              </template>
                            </TableColumn>
                          </DataTable>
                        </div>
                      </div>
                      <div class="promotions" v-if="hasUserPermission(quote, canViewPromotion)">
                        <div class="lead-promotions">{{ $t('worksheet.promotions') }}</div>
                        <div v-if="promoEditedData.index !== `${indexPre}${indexPreHotel}Pre`" class="flex">
                          <div class="w-full">
                            <HTMLEditor :readonly="true" v-model="preHotelItem.promotion" class="custom-editor" />
                          </div>
                          <span class="text-center">
                            <i v-if="hasUserPermission(quote, canEditPromotion)" class="pi pi-pencil edit-pi cursor-pointer" @click="promoEditedData = { index: `${indexPre}${indexPreHotel}Pre`, data: preHotelItem.promotion }"></i>
                          </span>
                        </div>
                        <div v-else class="flex">
                          <div class="w-full">
                            <HTMLEditor v-model="promoEditedData.data" class="custom-editor" />
                          </div>
                          <div class="text-center flex flex-col">
                            <i class="pi pi-check edit-pi cursor-pointer" @click="handleSavePromo(preHotelItem, 'promotion')"></i>
                            <i class="pi pi-times edit-pi cursor-pointer" @click="promoEditedData.index = null"></i>
                          </div>
                        </div>
                      </div>
                      <div class="agency-comment" v-if="hasUserPermission(quote, canViewAgencyComment)">
                        <div class="lead-comment">{{ $t('worksheet.agency_comment') }}</div>
                        <div v-if="editComment.index !== `${indexPre}${indexPreHotel}Pre`" class="flex">
                          <div class="w-full">
                            <HTMLEditor :readonly="true" v-model="preHotelItem.comment" class="custom-editor" />
                          </div>
                          <span class="text-center">
                            <i v-if="hasUserPermission(quote, canEditAgencyComment)" class="pi pi-pencil edit-pi cursor-pointer" @click="editComment = { index: `${indexPre}${indexPreHotel}Pre`, data: preHotelItem.comment }"></i>
                          </span>
                        </div>
                        <div v-else class="flex">
                          <div class="w-full">
                            <HTMLEditor v-model="editComment.data" class="custom-editor" />
                          </div>
                          <div class="text-center flex flex-col">
                            <i class="pi pi-check edit-pi cursor-pointer" @click="saveComment(preHotelItem, 'comment')"></i>
                            <i class="pi pi-times edit-pi cursor-pointer" @click="editComment.index = null"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="hotel-prefer mt-6" v-for="(hotelPrefer, indexHotelPrefer) in preHotelItem?.hotelPrefer" :key="indexHotelPrefer">
                      <div class="option" v-for="(room, index) in hotelPrefer?.rooms" :key="index">
                        <div class="title-hotel-prefer font-bold mb-3">{{ $t('worksheet.title_hotel_prefer') + ' - ' + hotelPrefer.hotel }}</div>
                        <div class="hotel-detail mt-3">
                          <div class="title-hotel font-bold mb-3 text-900">{{ $t('worksheet.hotel_detail') }}</div>
                          <div class="flex gap-6 flex-wrap md:flex-nowrap">
                            <div>
                              <img :src="preHotelItem?.hotelPreferPdf[indexHotelPrefer]?.imgUrl || fallBackImage" alt="" class="h-15rem w-22rem" />
                            </div>
                            <div class="min-w-20rem">
                              <div class="mb-1 font-bold text-lg text-900">{{ preHotelItem?.hotelPreferPdf[indexHotelPrefer]?.hotel }}</div>
                              <div class="mb-1">
                                <i v-for="(item, index) in parseInt(preHotelItem?.hotelPreferPdf[indexHotelPrefer]?.overallRate / 2)" :key="index" class="pi mr-1 pi-star-fill text-2xl text-color-secondary"></i>
                                <i v-if="+preHotelItem?.hotelPreferPdf[indexHotelPrefer]?.overallRate % 2 > 0 || true" class="text-color-secondary relative top-2px">
                                  <img :src="semistarImage" alt="star" />
                                </i>
                              </div>
                              <p class="text-sm text-900">{{ preHotelItem?.hotelPreferPdf[indexHotelPrefer]?.description }}</p>
                              <div class="text-900 text-sm">
                                <a href="javascript:" class="text-lg" @click="handleRedirectLink($event, preHotelItem?.hotelPreferPdf[indexHotelPrefer]?.urlDetail)">{{ $t('worksheet.hotel_detail') }}</a>
                                ({{ $t('worksheet.hint') }})
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="promotions" v-if="hasUserPermission(quote, canViewPromotion)">
                          <div class="lead-promotions">{{ $t('worksheet.promotions') }}</div>
                          <div v-if="promoEditedData.index !== `${indexHotelPrefer}PreferHotelPre`" class="flex">
                            <div class="w-full">
                              <HTMLEditor :readonly="true" v-model="hotelPrefer.promotion" class="custom-editor" />
                            </div>
                            <span class="text-center">
                              <i v-if="hasUserPermission(quote, canEditPromotion)" class="pi pi-pencil edit-pi cursor-pointer" @click="promoEditedData = { index: `${indexHotelPrefer}PreferHotelPre`, data: hotelPrefer.promotion }"></i>
                            </span>
                          </div>
                          <div v-else class="flex">
                            <div class="w-full">
                              <HTMLEditor v-model="promoEditedData.data" class="custom-editor" />
                            </div>
                            <div class="text-center flex flex-col">
                              <i class="pi pi-check edit-pi cursor-pointer" @click="handleSavePromo(hotelPrefer, 'promotion')"></i>
                              <i class="pi pi-times edit-pi cursor-pointer" @click="promoEditedData.index = null"></i>
                            </div>
                          </div>
                        </div>
                        <div class="agency-comment" v-if="hasUserPermission(quote, canViewAgencyComment)">
                          <div class="lead-comment">{{ $t('worksheet.agency_comment') }}</div>
                          <div v-if="editComment.index !== `${indexHotelPrefer}PreferHotelPre`" class="flex">
                            <div class="w-full">
                              <HTMLEditor :readonly="true" v-model="hotelPrefer.comment" class="custom-editor" />
                            </div>
                            <span class="text-center">
                              <i v-if="hasUserPermission(quote, canEditAgencyComment)" class="pi pi-pencil edit-pi cursor-pointer" @click="editComment = { index: `${indexHotelPrefer}PreferHotelPre`, data: hotelPrefer.comment }"></i>
                            </span>
                          </div>
                          <div v-else class="flex">
                            <div class="w-full">
                              <HTMLEditor v-model="editComment.data" class="custom-editor" />
                            </div>
                            <div class="text-center flex flex-col">
                              <i class="pi pi-check edit-pi cursor-pointer" @click="saveComment(hotelPrefer, 'comment')"></i>
                              <i class="pi pi-times edit-pi cursor-pointer" @click="editComment.index = null"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- post hotel -->
              <div id="post-hotel" v-if="hasUserPermission(quote, authorizations?.worksheet_cruise?.post_hotel_view)">
                <div v-for="(postItem, indexPost) in cruise.postHotel" :key="indexPost">
                  <div>
                    <HotelInform
                      :optionData="postItem"
                      :permission="canEditHeader"
                      :hotelWorkSheet="quote"
                      v-model:editAdult="editAdultPostHotel"
                      v-model:editChild="editChildPostHotel"
                      v-model:editNumberOfRoom="editNumberOfRoomPostHotel"
                      @handleSaveAdult="(adult) => handleSaveAdult(adult, postItem, 'adt', editAdultPostHotel)"
                      @handleSaveChild="(child) => handleSaveChild(child, postItem, 'chd', editChildPostHotel)"
                      @handleSaveNumberOfRoom="(numOfRoom) => handleSaveNumberOfRoom(numOfRoom, postItem, 'numOfRooms', editNumberOfRoomPostHotel)"
                      :isCruise="true"
                      :hotelTitle="$t('create_quote.post_hotel')"
                    />
                  </div>
                  <div v-for="(postHotelItem, indexPostHotel) in postItem.hotels" :key="indexPostHotel">
                    <div>
                      <div class="lead-option">{{ $t('worksheet.hotel') }}</div>
                      <div class="detail-option flex justify-content-between flex-wrap lg:flex-nowrap">
                        <div>
                          <div class="destination font-bold">
                            <div class="flex gap-3 mb-2 mt-2">
                              <div class="min-w-label">{{ $t('common.destination') }}:</div>
                              <div class="font-normal">{{ postItem?.airportCode }} ({{ postItem.dest || '' }})</div>
                            </div>
                          </div>
                          <div class="hotel font-bold">
                            <div class="flex gap-3 mb-2">
                              <div class="min-w-label">{{ $t('worksheet.hotel') }}:</div>
                              <div class="font-normal">{{ postHotelItem.hotel || '' }}</div>
                            </div>
                          </div>
                          <div class="hotel-room-cat font-bold mb-2 flex-1 mt-2">
                            <div class="flex gap-3 mb-2">
                              <div class="min-w-label">{{ $t('worksheet.hotel_room_category') }}:</div>
                              <div class="font-normal max-w-70 white-space-pre-line">{{ getRoomsName(postHotelItem) }}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="pricing" v-if="hasUserPermission(quote, authorizations?.worksheet_cruise?.post_hotel_pricing_view)">
                        <div class="option" v-for="(room, index) in postHotelItem.rooms" :key="index">
                          <div class="lead-pricing">{{ $t('common.pricing') }} ({{ getTextVariant(currentLanguage, room.longName, room.longNameFr) }})</div>
                          <DataTable
                            v-model:editingRows="editingRowsHotelPost[cruiseIndex][indexPost][indexPostHotel][index]"
                            :value="getHotelPrice(room.price)"
                            class="w-full"
                            editMode="row"
                            showGridlines
                            @row-edit-save="changePriceHotel($event, room.price)"
                          >
                            <TableColumn header="" dataType="string">
                              <template #body="{ data }">
                                {{ data.name && $t(data.name) }}
                              </template>
                            </TableColumn>
                            <TableColumn field="singlePrice" :header="$t('book_form.single')" dataType="number">
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                              </template>
                              <template #body="{ data, field, index }">
                                <div v-if="index !== 0">
                                  {{ data[field] ? (Number(data[field]).toFixed(2) ?? data[field]) : '0.00' }}
                                </div>
                              </template>
                            </TableColumn>
                            <TableColumn field="doublePrice" :header="$t('book_form.double')" dataType="number">
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                              </template>
                              <template #body="{ data, field, index }">
                                <div v-if="index !== 0">
                                  {{ data[field] ? (Number(data[field]).toFixed(2) ?? data[field]) : '0.00' }}
                                </div>
                              </template>
                            </TableColumn>
                            <TableColumn field="triplePrice" :header="$t('book_form.triple')" dataType="number">
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                              </template>
                              <template #body="{ data, field, index }">
                                <div v-if="index !== 0">
                                  {{ data[field] ? (Number(data[field]).toFixed(2) ?? data[field]) : '0.00' }}
                                </div>
                              </template>
                            </TableColumn>
                            <TableColumn field="quadPrice" :header="$t('book_form.quad')" dataType="number">
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                              </template>
                              <template #body="{ data, field, index }">
                                <div v-if="index !== 0">
                                  {{ data[field] ? (Number(data[field]).toFixed(2) ?? data[field]) : '0.00' }}
                                </div>
                              </template>
                            </TableColumn>
                            <TableColumn field="fiveInRoomPrice" :header="$t('book_form.5_in_room')" dataType="number">
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                              </template>
                              <template #body="{ data, field, index }">
                                <div v-if="index !== 0">
                                  {{ data[field] ? (Number(data[field]).toFixed(2) ?? data[field]) : '0.00' }}
                                </div>
                              </template>
                            </TableColumn>
                            <TableColumn field="sixInRoomPrice" :header="$t('book_form.6_in_room')" dataType="number">
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" />
                              </template>
                              <template #body="{ data, field, index }">
                                <div v-if="index !== 0">
                                  {{ data[field] ? (Number(data[field]).toFixed(2) ?? data[field]) : '0.00' }}
                                </div>
                              </template>
                            </TableColumn>
                            <TableColumn field="childOnePrice" :header="$t('common.child')" dataType="number">
                              <template #body="{ data, field, index }">
                                <div v-if="index === 0">
                                  {{ data[field] }}
                                </div>
                                <div v-else>
                                  <span>{{ data[field] ? (Number(data[field]).toFixed(2) ?? data[field]) : '0.00' }}</span>
                                </div>
                              </template>
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                                <InputText v-else v-model="data[field]" class="w-full" />
                              </template>
                            </TableColumn>
                            <TableColumn field="childTwoPrice" :header="$t('common.child')" dataType="number">
                              <template #body="{ data, field, index }">
                                <div v-if="index === 0">
                                  {{ data[field] }}
                                </div>
                                <div v-else>
                                  <span>{{ data[field] ? (Number(data[field]).toFixed(2) ?? data[field]) : '0.00' }}</span>
                                </div>
                              </template>
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                                <InputText v-else v-model="data[field]" class="w-full" @input="handleChildCruiseInput(data[field], field)" />
                              </template>
                            </TableColumn>
                            <TableColumn field="childThreePrice" :header="$t('common.child')" dataType="number">
                              <template #body="{ data, field, index }">
                                <div v-if="index === 0">
                                  {{ data[field] }}
                                </div>
                                <div v-else>
                                  <span>{{ data[field] ? (Number(data[field]).toFixed(2) ?? data[field]) : '0.00' }}</span>
                                </div>
                              </template>
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                                <InputText v-else v-model="data[field]" class="w-full" @input="handleChildCruiseInput(data[field], field)" />
                              </template>
                            </TableColumn>
                            <TableColumn field="infantPrice" :header="$t('common.infant')" dataType="number">
                              <template #body="{ data, field, index }">
                                <div v-if="index === 0">
                                  {{ data[field] }}
                                </div>
                                <div v-else>
                                  <span>{{ data[field] ? (Number(data[field]).toFixed(2) ?? data[field]) : '0.00' }}</span>
                                </div>
                              </template>
                              <template #editor="{ data, field, index }">
                                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                                <InputText v-else v-model="data[field]" class="w-full" @input="handleChildCruiseInput(data[field], field)" />
                              </template>
                            </TableColumn>
                            <TableColumn v-if="hasUserPermission(quote, authorizations?.worksheet_cruise?.post_hotel_pricing_edit)" :rowEditor="true" :header="$t('common.action')" class="w-8rem text-center">
                              <template #body="{ editorInitCallback, data }">
                                <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                                  <i v-if="data.key !== 'total'" class="pi pi-pencil" @click="editorInitCallback"></i>
                                </div>
                                <p v-if="data.key === 'total'" class="opacity-0"></p>
                              </template>
                              <template #editor="{ data, editorSaveCallback, editorCancelCallback }">
                                <i class="pi pi-check edit-pi" @click="handleSaveChildInput(editorSaveCallback, data)"></i>
                                <i class="pi pi-times edit-pi" @click="handleCancleChildInput(editorCancelCallback)"></i>
                              </template>
                            </TableColumn>
                          </DataTable>
                        </div>
                      </div>
                      <div class="promotions" v-if="hasUserPermission(quote, canViewPromotion)">
                        <div class="lead-promotions">{{ $t('worksheet.promotions') }}</div>
                        <div v-if="promoEditedData.index !== `${indexPost}${indexPostHotel}Post`" class="flex">
                          <div class="w-full">
                            <HTMLEditor :readonly="true" v-model="postHotelItem.promotion" class="custom-editor" />
                          </div>
                          <span class="text-center">
                            <i v-if="hasUserPermission(quote, canEditPromotion)" class="pi pi-pencil edit-pi cursor-pointer" @click="promoEditedData = { index: `${indexPost}${indexPostHotel}Post`, data: postHotelItem.promotion }"></i>
                          </span>
                        </div>
                        <div v-else class="flex">
                          <div class="w-full">
                            <HTMLEditor v-model="promoEditedData.data" class="custom-editor" />
                          </div>
                          <div class="text-center flex flex-col">
                            <i class="pi pi-check edit-pi cursor-pointer" @click="handleSavePromo(postHotelItem, 'promotion')"></i>
                            <i class="pi pi-times edit-pi cursor-pointer" @click="promoEditedData.index = null"></i>
                          </div>
                        </div>
                      </div>
                      <div class="agency-comment" v-if="hasUserPermission(quote, canViewAgencyComment)">
                        <div class="lead-comment">{{ $t('worksheet.agency_comment') }}</div>
                        <div v-if="editComment.index !== `${indexPost}${indexPostHotel}Post`" class="flex">
                          <div class="w-full">
                            <HTMLEditor :readonly="true" v-model="postHotelItem.comment" class="custom-editor" />
                          </div>
                          <span class="text-center">
                            <i v-if="hasUserPermission(quote, canEditAgencyComment)" class="pi pi-pencil edit-pi cursor-pointer" @click="editComment = { index: `${indexPost}${indexPostHotel}Post`, data: postHotelItem.comment }"></i>
                          </span>
                        </div>
                        <div v-else class="flex">
                          <div class="w-full">
                            <HTMLEditor v-model="editComment.data" class="custom-editor" />
                          </div>
                          <div class="text-center flex flex-col">
                            <i class="pi pi-check edit-pi cursor-pointer" @click="saveComment(postHotelItem, 'comment')"></i>
                            <i class="pi pi-times edit-pi cursor-pointer" @click="editComment.index = null"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="hotel-prefer mt-6" v-for="(hotelPrefer, indexHotelPrefer) in postHotelItem?.hotelPrefer" :key="indexHotelPrefer">
                      <div class="option" v-for="(room, index) in hotelPrefer?.rooms" :key="index">
                        <div class="title-hotel-prefer font-bold mb-3">{{ $t('worksheet.title_hotel_prefer') + ' - ' + hotelPrefer.hotel }}</div>
                        <div class="hotel-detail mt-3">
                          <div class="title-hotel font-bold mb-3 text-900">{{ $t('worksheet.hotel_detail') }}</div>
                          <div class="flex gap-6 flex-wrap md:flex-nowrap">
                            <div>
                              <img :src="postHotelItem?.hotelPreferPdf[indexHotelPrefer]?.imgUrl || fallBackImage" alt="" class="height-150px width-220px" />
                            </div>
                            <div class="min-w-20rem">
                              <div class="mb-1 font-bold text-lg text-900">{{ postHotelItem?.hotelPreferPdf[indexHotelPrefer]?.hotel }}</div>
                              <div class="mb-1">
                                <i v-for="(item, index) in parseInt(postHotelItem?.hotelPreferPdf[indexHotelPrefer]?.overallRate / 2)" :key="index" class="pi mr-1 pi-star-fill text-2xl text-color-secondary"></i>
                                <i v-if="+postHotelItem?.hotelPreferPdf[indexHotelPrefer]?.overallRate % 2 > 0 || true" class="text-color-secondary relative top-2px">
                                  <img :src="semistarImage" alt="star" />
                                </i>
                              </div>
                              <p class="text-sm text-900">{{ postHotelItem?.hotelPreferPdf[indexHotelPrefer]?.description }}</p>
                              <div class="text-900 text-sm">
                                <a href="javascript:" class="text-lg" @click="handleRedirectLink($event, postHotelItem?.hotelPreferPdf[indexHotelPrefer]?.urlDetail)">{{ $t('worksheet.hotel_detail') }}</a>
                                ({{ $t('worksheet.hint') }})
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="promotions" v-if="hasUserPermission(quote, canViewPromotion)">
                          <div class="lead-promotions">{{ $t('worksheet.promotions') }}</div>
                          <div v-if="promoEditedData.index !== `${indexHotelPrefer}PreferHotelPost`" class="flex">
                            <div class="w-full">
                              <HTMLEditor :readonly="true" v-model="hotelPrefer.promotion" class="custom-editor" />
                            </div>
                            <span class="text-center">
                              <i v-if="hasUserPermission(quote, canEditPromotion)" class="pi pi-pencil edit-pi cursor-pointer" @click="promoEditedData = { index: `${indexHotelPrefer}PreferHotelPost`, data: hotelPrefer.promotion }"></i>
                            </span>
                          </div>
                          <div v-else class="flex">
                            <div class="w-full">
                              <HTMLEditor v-model="promoEditedData.data" class="custom-editor" />
                            </div>
                            <div class="text-center flex flex-col">
                              <i class="pi pi-check edit-pi cursor-pointer" @click="handleSavePromo(hotelPrefer, 'promotion')"></i>
                              <i class="pi pi-times edit-pi cursor-pointer" @click="promoEditedData.index = null"></i>
                            </div>
                          </div>
                        </div>
                        <div class="agency-comment" v-if="hasUserPermission(quote, canViewAgencyComment)">
                          <div class="lead-comment">{{ $t('worksheet.agency_comment') }}</div>
                          <div v-if="editComment.index !== `${indexHotelPrefer}PreferHotelPost`" class="flex">
                            <div class="w-full">
                              <HTMLEditor :readonly="true" v-model="hotelPrefer.comment" class="custom-editor" />
                            </div>
                            <span class="text-center">
                              <i v-if="hasUserPermission(quote, canEditAgencyComment)" class="pi pi-pencil edit-pi cursor-pointer" @click="editComment = { index: `${indexHotelPrefer}PreferHotelPost`, data: hotelPrefer.comment }"></i>
                            </span>
                          </div>
                          <div v-else class="flex">
                            <div class="w-full">
                              <HTMLEditor v-model="editComment.data" class="custom-editor" />
                            </div>
                            <div class="flex flex-col text-center">
                              <i class="pi pi-check edit-pi cursor-pointer" @click="saveComment(hotelPrefer, 'comment')"></i>
                              <i class="pi pi-times edit-pi cursor-pointer" @click="editComment.index = null"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- transfer view -->
              <div class="option cruise-tranfer" v-if="hasUserPermission(quote, authorizations?.worksheet_cruise?.transfer_view)">
                <div class="lead-option">{{ $t('worksheet.transfers') }}</div>
                <div v-if="!editTransfer.bool" class="flex text-center">
                  <InputTextAera v-model="cruise.transfers" class="w-full" disabled />
                  <i v-if="hasUserPermission(quote, authorizations?.worksheet_cruise?.transfer_edit)" class="pi pi-pencil edit-pi cursor-pointer" @click="editTransfer = { bool: true, data: cruise.transfers }"></i>
                </div>
                <div v-else class="flex text-center">
                  <InputTextAera v-model="editTransfer.data" class="w-full" />
                  <div class="flex flex-col text-center">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="saveTransfer(cruise)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="editTransfer.bool = false"></i>
                  </div>
                </div>
              </div>
            </TabPanel>
            <TabPanel v-if="hasUserPermission(quote, canViewTermConditions)" :value="quote?.cruises.length">
              <div class="term_of_c">
                <div class="intro">
                  <h4 class="font-bold text-lg">{{ $t('common.intro') }}</h4>
                  <div v-if="!editIntro.isEdit" class="flex">
                    <div class="w-full">
                      <HTMLEditor :readonly="true" v-model="quote.intro" class="custom-editor" />
                    </div>
                    <span class="text-center">
                      <i v-if="hasUserPermission(quote, canEditTermConditions)" class="pi pi-pencil edit-pi cursor-pointer" @click="editIntro = { isEdit: true, data: quote.intro }"></i>
                    </span>
                  </div>
                  <div v-else class="flex">
                    <div class="w-full">
                      <HTMLEditor v-model="editIntro.data" class="custom-editor" />
                    </div>
                    <div class="text-center flex flex-col">
                      <i class="pi pi-check edit-pi cursor-pointer" @click="handleSaveIntro(quote)"></i>
                      <i class="pi pi-times edit-pi cursor-pointer" @click="editIntro.isEdit = false"></i>
                    </div>
                  </div>
                </div>
                <div class="term_conditions mt-4">
                  <h4 class="font-bold text-lg">{{ $t('menu.term_and_condition') }}</h4>
                  <div v-if="!editTermConditions.isEdit" class="flex">
                    <div class="w-full">
                      <HTMLEditor :readonly="true" v-model="quote.termConditions" class="custom-editor" />
                    </div>
                    <span class="text-center">
                      <i v-if="hasUserPermission(quote, canEditTermConditions)" class="pi pi-pencil edit-pi cursor-pointer" @click="editTermConditions = { isEdit: true, data: quote.termConditions }"></i>
                    </span>
                  </div>
                  <div v-else class="flex">
                    <div class="w-full">
                      <HTMLEditor v-model="editTermConditions.data" class="custom-editor" />
                    </div>
                    <div class="text-center flex flex-col">
                      <i class="pi pi-check edit-pi cursor-pointer" @click="handleSaveTermCondition(quote)"></i>
                      <i class="pi pi-times edit-pi cursor-pointer" @click="editTermConditions.isEdit = false"></i>
                    </div>
                  </div>
                </div>
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>
        <div class="flex justify-content-center mt-2 gap-3" v-if="hasUserPermission(quote, true)">
          <Button id="cruise-save-btn" :label="$t('promos.save')" icon="pi pi-check" class="p-button action-button" @click="handleUpdateQuote(quote)" />
          <ViewPDFFile :quoteID="quote.sk" :disabled="disablePreviewPDF" isShowBtn></ViewPDFFile>
        </div>
      </section>
    </div>
  </div>
</template>
<style lang="scss" scoped>
:deep() {
  .p-inputtextarea:disabled {
    opacity: 1;
    cursor: not-allowed;
  }

  .flight-detail {
    table {
      td,
      th {
        padding: 1rem 0.75rem !important;
      }
    }
  }
}

:deep(.p-inputnumber-input) {
  width: 100%;
}
</style>
